import i18n from '@/i18n';
import {
  taskAPI,
  bumpImageSpaceUsageCounts,
  infringementAPI,
  sourcingAPI,
  openReceiveAPI,
} from '@/utils/api';
import { normalizeListingAutoRule } from './listingAutoRule';
import {
  extractUrlsFromFundingTask,
  waitForFundingTaskComplete,
} from './waitForFundingTask';
import { convertImageToServerUrl, normalizePersistedImageUrl, stripImageFileExtension } from '@/utils/imageUrlUtils';
import {
  isGenericListingImageName,
  isOpaqueStorageBaseName,
} from '@/pages/ipe/workbench/utils/platformListingItemMedia';
import { pollInfringementBatch } from './infringementBatchPoll';
import {
  toProcessableImageUrl,
  ensureInitialSpace,
  persistProcessedImageToSpace,
  attachWorkflowImagePreview,
} from './sourcingWorkflowSpace';
import { resolveImg2ImgReferenceForApi } from './img2imgPublicUrl';
import { buildPipelineReferenceResult } from '@/pages/ipe/workbench/constants/fundingTaskImageUrlFields';
import { runGoodsAiStudioWorkflowStep, runGoodsWorkflowStep, runGoodsBatchWorkflowStep } from './sourcingGoodsWorkflow';
import {
  buildColorSlotsForGoodsPrintGroup,
  overlayGoodsStudioOnColorSlots,
} from './listingBridgeColorSlots';
import { fetchAllCompositeImageRows, mergeCompositeImagesIntoGoodsForms } from './goodsDetailCompositeMerge';
import {
  runAiCopywritingWorkflowStep,
  runAiImageWorkflowStep,
  runModelStudioWorkflowStep,
  isAiImageWorkflowNav,
  runAiImageBatchWorkflowStep,
  runModelStudioBatchWorkflowStep,
  runAiCopywritingBatchWorkflowStep,
  runAiTextToImageBatchWorkflowStep,
  runFissionBatchWorkflowStep,
  groupOutputUrlsBySource,
} from './sourcingAiStudioWorkflow';
import { runVideoWorkflowStep } from './sourcingVideoWorkflow';
import { isModelStudioNav, MODEL_STUDIO_BATCH_CONCURRENT } from '@/pages/ipe/workbench/constants/modelStudioConfig';
import {
  resolveModelTryOnModelPreviewUrl,
  resolveTryOnReuseModelState,
} from '@/pages/ipe/workbench/constants/modelTryOnPresetConfig';
import {
  resolveModelEthnicityModelPreviewUrl,
  resolveModelEthnicityReuseModelState,
} from '@/pages/ipe/workbench/utils/systemModelEthnicityPresetCatalog';
import { resolveStampSlotNames } from '@/pages/ipe/workbench/utils/podTemplateMeta';
import {
  STAMP_ASSIGN_MODE,
  resolveStampAssignMode,
  resolvePreAssignExecutionMode,
  getNeededRowIdsForPreAssign,
  resolvePreAssignExecutorRowIndex,
  buildStampSlotAssignments,
  buildPreAssignGoodsForms,
  buildPerRowPreAssignGoodsForms,
  loadWorkflowStampTemplates,
} from '@/pages/ipe/workbench/utils/workflowStampPreAssign';
import {
  registerPreAssignRowStampOutput,
  waitForPreAssignRowStampOutputs,
  waitForPreAssignGoodsComplete,
  notifyPreAssignGoodsComplete,
} from '@/pages/ipe/workbench/utils/workflowInteractive';
import { resolveStepParams } from './workflowStepParams';
import { resolveWorkflowNavId, WORKFLOW_EXECUTABLE_NAV_IDS } from '@/pages/ipe/workbench/constants/workflowNavId';
import {
  STEP_FAILURE_POLICY_SKIP,
  normalizeStepFailurePolicy,
} from '@/pages/ipe/workbench/constants/workflowStepFailurePolicy';
import { persistSmartCutoutModuleImageSpace } from './workflowModuleSideEffects';
import { runStudioWorkerWorkflowStep } from './studioWorkerWorkflow';
import {
  IMAGE_CROP_WORKER_MODE,
  submitImageCropTask,
  pollImageCropBatch,
  pickCroppedUrlFromRow,
} from '@/image-crop';
import {
  beginWorkflowFeatureTask,
  completeWorkflowFeatureImageTask,
  failWorkflowFeatureTask,
} from './workflowFeatureTask';
import {
  resolveTemplateStepStages,
  alignResumeStepIndex,
  resolveTemplateExecutionPlan,
  resolveWorkflowStepFlatIndex,
  buildBranchExecutionTemplate,
} from './workflowStepPlan';
import { extractStampExtractResultUrl, runSmartCutoutProcess } from './imageProcessStudioSubmit';

function throwIfAborted(shouldAbort) {
  if (typeof shouldAbort === 'function' && shouldAbort()) {
    const err = new Error('任务已终止');
    err.name = 'WorkflowTaskAbortedError';
    throw err;
  }
}

/** 步骤失败且模板设置为「终止整批」时抛出：中断当前批次与后续条目 */
function createBatchTerminateError(step, nav, message, result) {
  const label = step?.label || nav || i18n.t('workbench:ipeUtilError.currentStep');
  const err = new Error(
    message
    || i18n.t('workbench:ipeUtilError.stepFailedTerminate', { label }),
  );
  err.name = 'WorkflowBatchTerminatedError';
  err.batchTerminated = true;
  err.stepLabel = label;
  if (result) err.result = result;
  return err;
}

/** 需向上抛出、中断整批的错误（用户终止 / 步骤失败终止整批） */
function isFatalPipelineError(err) {
  return Boolean(
    err
    && (err.name === 'WorkflowTaskAbortedError'
      || err.message === '任务已终止'
      || err.batchTerminated
      || err.name === 'WorkflowBatchTerminatedError'),
  );
}

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
/** 单批并发执行的条目数（与模特试衣等 AI Studio 批量上限对齐，多图同任务并行处理） */
const MAX_BATCH_CONCURRENT = MODEL_STUDIO_BATCH_CONCURRENT;
/** 批次间延迟（毫秒），避免连续批次触发限流 */
const BATCH_DELAY_MS = 5000;
/** 单个条目内步骤间延迟（毫秒），避免单个条目内部过快 */
const STEP_DELAY_MS = 4000;
/** 限流重试次数 */
const RATE_LIMIT_RETRY_TIMES = 5;
/** 瞬态错误（网络/5xx）重试次数 */
const TRANSIENT_RETRY_TIMES = 2;

/** 限流重试基础延迟（毫秒），实际等待随重试次数递增 */
const RATE_LIMIT_RETRY_DELAY_MS = 8000;
/** 瞬态错误重试基础延迟（毫秒） */
const TRANSIENT_RETRY_DELAY_MS = 3000;

/** 随机抖动范围（毫秒），避免请求同步触发 */
const JITTER_MS = 800;

/** 带抖动的延迟函数 */
async function sleepWithJitter(baseMs) {
  const jitter = Math.random() * JITTER_MS;
  await sleep(baseMs + jitter);
}

function isRateLimitedError(err) {
  if (!err) return false;
  if (err.status === 429) return true;
  if (String(err.code || '').toUpperCase() === 'TOO_MANY_REQUESTS') return true;
  const msg = String(err.message || err.reason || '');
  return (
    msg.includes('请求过于频繁') ||
    msg.includes('429') ||
    /too many requests/i.test(msg)
  );
}

function isTransientWorkflowError(err) {
  if (!err || isNonRetryableWorkflowError(err)) return false;
  if (isRateLimitedError(err)) return true;
  const status = Number(err.status || err.statusCode || err.httpStatus || 0);
  if ([408, 502, 503, 504].includes(status)) return true;
  const msg = String(err.message || err.reason || '').toLowerCase();
  if (
    msg.includes('timeout') ||
    msg.includes('timed out') ||
    msg.includes('network') ||
    msg.includes('fetch') ||
    msg.includes('econnreset') ||
    msg.includes('failed to fetch') ||
    msg.includes('load failed') ||
    msg.includes('socket') ||
    (msg.includes('aborted') && msg.includes('request'))
  ) {
    return true;
  }
  // 服务端把模型侧异常转成中文提示（AI 文案「生成超时」「模型连接被重置」等），同样属于可重试瞬态
  return isTransientChineseHint(msg);
}

/** 中文瞬态提示：模型超时 / 连接重置 / 服务繁忙等，重试一次通常可恢复 */
function isTransientChineseHint(msg) {
  if (!msg) return false;
  const transientHints = [
    '超时',
    '稍后重试',
    '连接被重置',
    '连接重置',
    '服务繁忙',
    '系统繁忙',
    '网络异常',
    '网络错误',
    '请求过于频繁',
  ];
  return transientHints.some((hint) => msg.includes(hint));
}

function isNonRetryableWorkflowError(err) {
  if (!err) return false;
  if (err.name === 'WorkflowTaskAbortedError' || err?.message === '任务已终止') return true;
  if (err.batchTerminated || err.name === 'WorkflowBatchTerminatedError') return true;
  if (err.skipRow || err.name === 'WorkflowRowSkippedError') return true;
  const msg = String(err.message || err.reason || '');
  if (msg.includes('无法自动执行') || msg.includes('未实现自动执行') || msg.includes('暂不支持')) return true;
  if (msg.includes('请在工作流中将') || msg.includes('流水线已中止')) return true;
  return false;
}

/**
 * 工作流步骤执行重试：429 限流 + 网络/5xx 等瞬态错误自动重试
 */
async function withWorkflowStepRetry(fn, { rateLimitRetries = RATE_LIMIT_RETRY_TIMES, transientRetries = TRANSIENT_RETRY_TIMES } = {}) {
  let rateAttempts = 0;
  let transientAttempts = 0;
  while (true) {
    try {
      return await fn();
    } catch (err) {
      if (isNonRetryableWorkflowError(err)) throw err;
      if (isRateLimitedError(err) && rateAttempts < rateLimitRetries) {
        rateAttempts += 1;
        const delay = RATE_LIMIT_RETRY_DELAY_MS * rateAttempts;
        console.warn(`[工作流限流重试] 第 ${rateAttempts}/${rateLimitRetries} 次，等待 ~${delay}ms…`, err.message || err);
        await sleepWithJitter(delay);
        continue;
      }
      if (isTransientWorkflowError(err) && transientAttempts < transientRetries) {
        transientAttempts += 1;
        const delay = TRANSIENT_RETRY_DELAY_MS * transientAttempts;
        console.warn(`[工作流瞬态重试] 第 ${transientAttempts}/${transientRetries} 次，等待 ~${delay}ms…`, err.message || err);
        await sleepWithJitter(delay);
        continue;
      }
      throw err;
    }
  }
}

function imageNaturalSize(url) {
  const u = String(url || '').trim();
  if (!u) return Promise.resolve({ width: 0, height: 0 });
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve({ width: img.naturalWidth || 0, height: img.naturalHeight || 0 });
    img.onerror = () => resolve({ width: 0, height: 0 });
    img.src = u.startsWith('data:') || u.startsWith('blob:') ? u : u;
  });
}

/** 预览区左右 URL 加轻量区分参数，避免 CDN/浏览器把两步当成同一资源；data/blob 不加。 */
function tracePreviewUrl(url, slot, sid) {
  const u = String(url || '').trim();
  if (!u || u.startsWith('data:') || u.startsWith('blob:')) return u;
  const sep = u.includes('?') ? '&' : '?';
  return `${u}${sep}_wfslot=${encodeURIComponent(slot || '')}&wfsid=${encodeURIComponent(String(sid || ''))}`;
}

function withStepOutputDisplay(record, ref) {
  const viewUrl = String(ref?.viewUrl || ref?.displayUrl || ref?.outputImageUrlDisplay || '').trim();
  const compositeImageViewUrls = Array.isArray(ref?.compositeImageViewUrls)
    ? ref.compositeImageViewUrls
    : (viewUrl ? [viewUrl] : []);
  if (!viewUrl && compositeImageViewUrls.length === 0) return record;
  return {
    ...record,
    viewUrl,
    displayUrl: viewUrl,
    outputImageUrlDisplay: viewUrl,
    compositeImageViewUrls,
  };
}

/**
 * 插件工作流「图案裁剪」：与 Studio / 详情页同一条 Qwen Worker 主链
 * （submitImageCropTask → FundingBatchJobService imageCropBatch）。
 */
async function runCropStep(row, currentSpaceId, currentUrl, workflowContext = {}, shouldAbort) {
  return runStudioWorkerWorkflowStep({
    navId: 'imageCrop',
    taskType: 'imageCrop',
    taskName: '图案裁剪',
    row,
    currentSpaceId,
    currentUrl,
    workflowContext,
    shouldAbort,
    persistLabel: '工作流-裁剪',
    initialResult: {
      engine: 'qwen-detect',
      cropMode: IMAGE_CROP_WORKER_MODE,
    },
  });
}

async function runCutoutStep(row, currentSpaceId, currentUrl, params = {}, workflowContext = {}, shouldAbort) {
  throwIfAborted(shouldAbort);
  const previewUrl = currentUrl || '';
  const cutoutMode = String(params?.cutoutMode || params?.mode || 'basic').toLowerCase() === 'advanced'
    ? 'advanced'
    : 'basic';
  const { taskId, initialResult } = await beginWorkflowFeatureTask({
    taskType: 'smartCutout',
    taskName: '智能抠图',
    imageIds: [currentSpaceId],
    previewUrl,
    estimatedTotal: 1,
    workflowContext,
    initialResult: {
      ...buildPipelineReferenceResult({
        imageUrl: previewUrl,
        imageId: currentSpaceId,
        previewUrl,
      }),
      cutoutMode,
      cutoutEngine: cutoutMode === 'advanced' ? 'qwen' : 'aliyun',
      engine: 'ipe-cutout',
      cutoutFormats: ['png'],
      outputFormat: 'png',
    },
  });
  try {
    const imgForApi = await toProcessableImageUrl(currentUrl);
    throwIfAborted(shouldAbort);
    if (!imgForApi) throw new Error(i18n.t('workbench:ipeUtilError.cutoutSourceUnresolved'));

    const subAccount = String(workflowContext?.workflowTaskId || row?.id || 'wf').slice(0, 40);
    const cutoutResult = await runSmartCutoutProcess(
      subAccount,
      { id: row.id },
      imgForApi,
      {
        formats: ['png'],
        mode: cutoutMode,
      },
    );

    throwIfAborted(shouldAbort);
    const outputUrl = cutoutResult?.url || extractStampExtractResultUrl(cutoutResult?.rawResult);
    if (!outputUrl || !String(outputUrl).trim()) {
      throw new Error(i18n.t('workbench:ipeUtilError.cutoutNoOutput'));
    }

    const dims = await imageNaturalSize(outputUrl);
    await persistSmartCutoutModuleImageSpace(taskId, currentSpaceId, outputUrl, {
      width: dims.width || undefined,
      height: dims.height || undefined,
    });
    const saved = await persistProcessedImageToSpace(`wf-cut-${row.id}`, outputUrl, '工作流-抠图', { navId: 'smartCutout' });
    const canonicalUrl = saved.imageUrl;
    await completeWorkflowFeatureImageTask(taskId, [canonicalUrl], 1, initialResult, {
      cutoutEngine: 'ipe',
      cutoutFormats: ['png'],
      outputFormat: 'png',
      note: '已在工作流中完成（智能抠图）',
      outputSpaceId: saved.spaceId,
      outputImageUrl: canonicalUrl,
      engine: cutoutResult?.engine || 'ipe-cutout-v2',
    }, cutoutResult?.rawResult);
    return { spaceId: saved.spaceId, imageUrl: canonicalUrl, taskId };
  } catch (e) {
    await failWorkflowFeatureTask(taskId, initialResult, 1, e);
    throw e;
  }
}

async function runEnhanceStep(row, currentSpaceId, currentUrl, params = {}, workflowContext = {}, shouldAbort) {
  try {
    await bumpImageSpaceUsageCounts([String(currentSpaceId)]);
  } catch (e) {
    console.warn('[workflow 超分提质] 使用次数更新失败', e);
  }
  return runStudioWorkerWorkflowStep({
    navId: 'imageEnhance',
    taskType: 'imageEnhance',
    taskName: '超分提质',
    row,
    currentSpaceId,
    currentUrl,
    workflowContext,
    shouldAbort,
    persistLabel: '工作流-放大',
    initialResult: {
      pipeline: 'qwen-clarity-v1',
      engine: 'qwen-image-2.0',
      localEnhanceUrls: {},
      localFailedImageIds: [],
    },
  });
}

/**
 * 印花图提取：灵图 /aigc/matting 抠图，写入独立图片空间供套图使用。
 */
async function runStampExtractStep(row, currentSpaceId, currentUrl, workflowContext = {}, shouldAbort) {
  throwIfAborted(shouldAbort);
  const previewUrl = currentUrl || '';
  const referenceUrl = await resolveImg2ImgReferenceForApi(currentUrl);
  if (!referenceUrl) throw new Error(i18n.t('workbench:ipeUtilError.stampExtractSourceUnresolved'));
  const { taskId, initialResult } = await beginWorkflowFeatureTask({
    taskType: 'stampExtract',
    taskName: '印花提取',
    imageIds: [currentSpaceId],
    previewUrl,
    estimatedTotal: 1,
    workflowContext,
    initialResult: {
      ...buildPipelineReferenceResult({
        imageUrl: referenceUrl,
        imageId: currentSpaceId,
        previewUrl,
      }),
      engine: 'qwen-image-2.0',
      serverWorker: true,
      batchPhase: 'queued',
    },
  });
  try {
    await taskAPI.submit(taskId);
    const task = await waitForFundingTaskComplete(taskId);
    const status = String(task?.status || '').toLowerCase();
    if (status === 'failed' || status === 'canceled' || status === 'cancelled') {
      throw new Error(task?.result?.failureHint || i18n.t('workbench:ipeUtilError.stampExtractFailed'));
    }
    const urls = extractUrlsFromFundingTask(task);
    const stampUrl = urls[0] || extractStampExtractResultUrl(task?.result || {});
    if (!stampUrl || !String(stampUrl).trim()) {
      throw new Error(i18n.t('workbench:ipeUtilError.stampExtractNoImage'));
    }

    const stampName = String(row?.title || row?.name || '').trim() || '工作流-印花提取';
    const saved = await persistProcessedImageToSpace(`wf-stamp-${row.id}`, stampUrl, stampName, { navId: 'stampExtract' });
    const canonicalUrl = saved.imageUrl;
    await completeWorkflowFeatureImageTask(taskId, [canonicalUrl], 1, initialResult, {
      outputSpaceId: saved.spaceId,
      outputImageUrl: canonicalUrl,
      engine: task?.result?.engine || 'qwen-image-2.0',
      prompt: task?.result?.prompt || task?.result?.effectivePrompt || '',
      note: '插件工作流：服务端队列提取印花',
    }, task?.result || {});
    return { spaceId: saved.spaceId, imageUrl: canonicalUrl, taskId };
  } catch (e) {
    await failWorkflowFeatureTask(taskId, initialResult, 1, e);
    throw e;
  }
}

/** 旧工作流「标题提取」节点：统一改为 AI 文案 */
async function runLegacyTitlesAsAiCopywriting(row, params, currentSpaceId, currentUrl, workflowContext = {}) {
  const copied = await runAiCopywritingWorkflowStep(
    row,
    currentSpaceId,
    currentUrl,
    {
      ...params,
      prompt: params.prompt || params.titlePrompt || '',
      titlePresetId: params.titlePresetId,
      copywritingLanguage: params.copywritingLanguage || params.targetLanguage,
    },
    workflowContext,
  );
  return {
    taskId: copied.taskId,
    titleText: copied.copywritingText,
    copywritingText: copied.copywritingText,
    language: copied.language,
    note: copied.note,
  };
}

async function resolveServerImageUrl(imageUrl) {
  const raw = String(imageUrl || '').trim();
  if (!raw) throw new Error(i18n.t('workbench:ipeUtilError.imageUrlUnresolved'));
  if (raw.startsWith('/uploads/') || raw.startsWith('uploads/')) {
    return normalizePersistedImageUrl(raw) || raw;
  }
  if (raw.startsWith('http') && !raw.startsWith('data:')) {
    const persisted = normalizePersistedImageUrl(raw);
    if (persisted) return persisted;
    return raw;
  }
  const data = raw.startsWith('data:') ? raw : await toProcessableImageUrl(raw);
  if (!data) throw new Error(i18n.t('workbench:ipeUtilError.imageReadFailed'));
  const server = await convertImageToServerUrl(data);
  if (!server) throw new Error(i18n.t('workbench:ipeUtilError.imageUploadServerFailed'));
  return normalizePersistedImageUrl(server) || server;
}

function summarizeInfringementRisk(detail) {
  const items = Array.isArray(detail?.items) ? detail.items : [];
  const counts = { high: 0, medium: 0, low: 0, unknown: 0 };
  items.forEach((it) => {
    const r = String(it?.riskLevel || 'unknown').toLowerCase();
    if (r === 'high') counts.high += 1;
    else if (r === 'medium') counts.medium += 1;
    else if (r === 'low') counts.low += 1;
    else counts.unknown += 1;
  });
  return { items, counts };
}

function isHighInfringementRisk(counts, topRisk) {
  if (Number(counts?.high) > 0) return true;
  return String(topRisk || '').toLowerCase() === 'high';
}

/** 工作流「侵权检测」：创建批次（异步）后轮询直至全部 item 终态 */
  async function runInfringementStep(row, currentUrl, params = {}, interactive = {}) {
  const mode = params.filterMode === 'deep' ? 'deep' : 'basic';
  const imageUrl = await resolveServerImageUrl(currentUrl);
  const created = await infringementAPI.createBatch({
    imageUrls: [imageUrl],
    mode,
    remark: `工作流 · 条目 ${row?.id ?? ''}`.trim(),
  });
  const batchId = created?.id;
  if (!batchId) throw new Error(i18n.t('workbench:ipeUtilError.infringementCreateFailed'));
  const detail = await pollInfringementBatch(batchId);
  const { items, counts } = summarizeInfringementRisk(detail);
  const topRisk = items[0]?.riskLevel || 'unknown';
  const advice = items[0]?.advice || '';
  const highRisk = isHighInfringementRisk(counts, topRisk);

  if (highRisk && typeof interactive.onInfringementRiskPrompt === 'function') {
    const decision = await interactive.onInfringementRiskPrompt({
      row,
      detail,
      items,
      counts,
      advice,
      batchId,
      batchNo: detail.batchNo,
      topRisk,
    });
    const stopCurrentRow =
      decision === 'skip' ||
      decision === 'stopItem' ||
      decision === 'terminate';
    if (stopCurrentRow) {
      const err = new Error(
          advice || '检测到较高侵权风险，已按您的选择终止当前条目。',
      );
      err.name = 'WorkflowRowSkippedError';
      err.skipRow = true;
      throw err;
    }
  } else if (params.blockOnHighRisk !== false && highRisk) {
    throw new Error(
        advice || i18n.t('workbench:ipeUtilError.highRiskAborted', { count: counts.high, batch: detail.batchNo || batchId }),
    );
  }

  const noteParts = [
    i18n.t('workbench:ipeUtilError.riskBatchNote', { batch: detail.batchNo || batchId }),
    i18n.t('workbench:ipeUtilError.riskCounts', { high: counts.high, medium: counts.medium, low: counts.low }),
  ];
  if (advice) noteParts.push(advice);
  if (highRisk && interactive.onInfringementRiskPrompt) {
    noteParts.push(i18n.t('workbench:ipeUtilError.userChoseContinue'));
  }
  return {
    batchId,
    batchNo: detail.batchNo,
    riskLevel: topRisk,
    highRisk,
    counts,
    note: noteParts.join(' · '),
  };
}


/** 读取自动规则（定时上架节点用于兜底 SHEIN 模板/类目，避免退化成人工刊登） */
async function fetchListingAutoRuleById(ruleId) {
  const id = String(ruleId || '').trim();
  if (!id) return null;
  try {
    // 自动规则归属 openReceiveAPI（/open-receive/auto-rules），仓库不存在独立 autoRuleAPI
    const res = await openReceiveAPI.getAutoRuleById(id);
    return normalizeListingAutoRule(res?.item || res);
  } catch {
    return null;
  }
}

function uniqueListingLabels(values) {
  const out = [];
  (Array.isArray(values) ? values : [values]).forEach((value) => {
    const label = String(value || '').trim();
    if (label && !out.includes(label)) out.push(label);
  });
  return out;
}

/** 本地商品库格子：规则名 + 数量 + 批次 */
function formatListingLibraryNote({ count, batchKey, ruleNames, associated } = {}) {
  const names = uniqueListingLabels(ruleNames);
  const parts = [];
  if (names.length > 0) parts.push(names.join(' · '));
  else if (associated) parts.push('已关联自动规则');
  const n = Number(count);
  if (Number.isFinite(n) && n > 0) parts.push(`数量 ${n}`);
  const batch = String(batchKey || '').trim();
  if (batch) parts.push(`批次 ${batch}`);
  return parts.join(' · ') || '已入库';
}

function canonicalizeListingUrl(url) {
  const raw = String(url || '').trim();
  if (!raw || raw.startsWith('data:') || raw.startsWith('blob:')) return raw;
  return raw
    .replace(/([?&])_wfslot=[^&]*/g, '$1')
    .replace(/([?&])wfsid=[^&]*/g, '$1')
    .replace(/\?&+/g, '?')
    .replace(/[?&]+$/g, '');
}

function uniqueListingUrls(urls = []) {
  const seen = new Set();
  const out = [];
  (Array.isArray(urls) ? urls : []).forEach((item) => {
    const url = canonicalizeListingUrl(item);
    if (!url || seen.has(url)) return;
    seen.add(url);
    out.push(url);
  });
  return out;
}

function findWorkflowResultStep(result, navIds) {
  const want = new Set((Array.isArray(navIds) ? navIds : [navIds]).map(String));
  const steps = Array.isArray(result?.steps) ? result.steps : [];
  for (let i = steps.length - 1; i >= 0; i -= 1) {
    const nav = resolveWorkflowNavId(steps[i]) || steps[i]?.navId;
    if (want.has(nav)) return steps[i];
  }
  return null;
}

function collectStepListingUrls(step) {
  if (!step) return [];
  return uniqueListingUrls([
    ...(Array.isArray(step.compositeImageUrls) ? step.compositeImageUrls : []),
    step.outputImageUrl,
    step.imageUrl,
  ]);
}

/**
 * 上游 POD 商品设计各组主图（黑/白等颜色位）+ 颜色元数据。
 * metas 与 urls 取并集，禁止因 metas 不完整而只留下第一张主图。
 */
function collectPodMainEntriesFromPrevResult(prev) {
  const pod = findWorkflowResultStep(prev, ['podProductDesign']);
  if (!pod) return [];
  const byUrl = new Map();
  const add = (rawUrl, meta = {}) => {
    const url = canonicalizeListingUrl(rawUrl) || String(rawUrl || '').trim();
    if (!url) return;
    const prevEntry = byUrl.get(url) || {
      url,
      colorLabel: '',
      colorCode: '',
      mainName: '',
    };
    byUrl.set(url, {
      url,
      colorLabel: String(meta.colorLabel || prevEntry.colorLabel || '').trim(),
      colorCode: String(meta.colorCode || prevEntry.colorCode || '').trim(),
      mainName: String(meta.mainName || prevEntry.mainName || '').trim(),
    });
  };
  (Array.isArray(pod.mainCompositeMetas) ? pod.mainCompositeMetas : []).forEach((row) => {
    add(row?.url, row);
  });
  uniqueListingUrls(
    Array.isArray(pod.mainCompositeImageUrls) ? pod.mainCompositeImageUrls : [],
  ).forEach((url) => add(url));
  return [...byUrl.values()];
}

/**
 * 主图创作展开前：若 POD 步骤上主图列表过短，从 POD 任务回读完整色位主图。
 */
async function enrichPodMainEntriesFromGoodsTasks(active, prevStageResult) {
  const taskSlotsCache = new Map();
  for (const i of active) {
    const prev = prevStageResult[i];
    const pod = findWorkflowResultStep(prev, ['podProductDesign']);
    if (!pod) continue;
    const existing = collectPodMainEntriesFromPrevResult(prev);
    if (existing.length >= 2) continue;
    const taskId = String(pod.goodsTaskId || pod.taskId || '').trim();
    if (!taskId) continue;
    const listingGroupIndex = Number(pod.listingGroupIndex);
    const cacheKey = `${taskId}:${Number.isFinite(listingGroupIndex) ? listingGroupIndex : 'auto'}`;
    let slots = taskSlotsCache.get(cacheKey);
    if (!slots) {
      try {
        slots = await loadPodColorSlotsFromGoodsTask(pod);
      } catch {
        slots = [];
      }
      taskSlotsCache.set(cacheKey, Array.isArray(slots) ? slots : []);
    }
    if (!Array.isArray(slots) || slots.length <= existing.length) continue;
    pod.mainCompositeImageUrls = slots.map((slot) => String(slot.mainUrl || '').trim()).filter(Boolean);
    pod.mainCompositeMetas = slots
      .map((slot) => ({
        url: String(slot.mainUrl || '').trim(),
        colorLabel: String(slot.colorLabel || '').trim(),
        colorCode: String(slot.colorCode || '').trim(),
        mainName: String(slot.mainName || slot.patternName || '').trim(),
      }))
      .filter((row) => row.url);
  }
}

function resolvePodListingGroupKey(prev, rowLocalIndex) {
  const pod = findWorkflowResultStep(prev, ['podProductDesign']);
  if (!pod) return `row:${rowLocalIndex}`;
  const listingGroupIndex = Number(pod.listingGroupIndex);
  if (!Number.isFinite(listingGroupIndex) || listingGroupIndex < 0) {
    return `row:${rowLocalIndex}`;
  }
  const taskKey = String(pod.goodsTaskId || pod.taskId || '').trim() || 'pod';
  return `lg:${taskKey}:${listingGroupIndex}`;
}

function pickWorkflowListingTitle(row, result, copyStep) {
  const candidates = [
    copyStep?.copywritingText,
    copyStep?.titleText,
    result?.copywritingText,
    result?.titleText,
    row?.title,
  ];
  for (const raw of candidates) {
    const text = String(raw || '').trim()
      .replace(/^(?:商品标题|产品标题|标题)\s*[:：]\s*/i, '')
      .trim();
    if (!text || text.length > 240) continue;
    if (/助手|模板|请严|优化|以下内容|电商标题/.test(text)) continue;
    return text.slice(0, 200);
  }
  return String(row?.id || '工作流商品').slice(0, 200) || '工作流商品';
}

/** 素材原名（上传/图片空间文件名），不是 AI 标题或系统占位名 */
function pickWorkflowMaterialName(row, fallbackUrl = '') {
  const fromRow = stripImageFileExtension(row?.title || row?.name || '');
  if (fromRow && !isGenericListingImageName(fromRow) && !isOpaqueStorageBaseName(fromRow)) {
    return fromRow;
  }
  const fromUrl = stripImageFileExtension(fileNameFromListingUrl(fallbackUrl));
  if (fromUrl && !isGenericListingImageName(fromUrl) && !isOpaqueStorageBaseName(fromUrl)) {
    return fromUrl;
  }
  return fromRow || '';
}

function fileNameFromListingUrl(url) {
  const raw = String(url || '').trim();
  if (!raw) return '';
  try {
    const parsed = new URL(raw, 'https://local.invalid');
    const key = parsed.searchParams.get('key') || parsed.searchParams.get('objectKey') || '';
    if (key) {
      const fromKey = decodeURIComponent((key.split('/').filter(Boolean).pop() || '').split('?')[0]);
      if (fromKey && fromKey !== 'view') return fromKey;
    }
    const base = decodeURIComponent((parsed.pathname.split('/').filter(Boolean).pop() || '').split('?')[0]);
    if (base && base !== 'view') return base;
    return '';
  } catch {
    const cleaned = raw.split('?')[0];
    const parts = cleaned.split('/').filter(Boolean);
    try {
      return decodeURIComponent(parts[parts.length - 1] || '');
    } catch {
      return parts[parts.length - 1] || '';
    }
  }
}

function pickReadableListingImageName(...candidates) {
  for (const raw of candidates) {
    const name = stripImageFileExtension(raw);
    if (name && !isGenericListingImageName(name) && !isOpaqueStorageBaseName(name)) return name;
  }
  return '';
}

function resolveListingImageName(candidates, fallback) {
  return pickReadableListingImageName(...candidates) || String(fallback || '').trim();
}

function listingUrlKey(url) {
  const raw = String(url || '').trim();
  if (!raw) return '';
  const uploadsAt = raw.indexOf('/uploads/');
  if (uploadsAt >= 0) return raw.slice(uploadsAt).split('?')[0];
  return raw.split('?')[0];
}

function sameListingUrl(a, b) {
  const left = listingUrlKey(a);
  const right = listingUrlKey(b);
  return Boolean(left) && left === right;
}

function compactWorkflowColorSlot(slot) {
  const mainUrl = String(slot?.mainUrl || '').trim();
  if (!mainUrl) return null;
  const detailUrls = [];
  const detailNames = [];
  const rawDetails = Array.isArray(slot?.detailUrls) ? slot.detailUrls : [];
  const rawNames = Array.isArray(slot?.detailNames) ? slot.detailNames : [];
  rawDetails.forEach((raw, index) => {
    const url = String(raw || '').trim();
    if (!url || sameListingUrl(url, mainUrl)) return;
    if (detailUrls.some((existing) => sameListingUrl(existing, url))) return;
    detailUrls.push(url);
    detailNames.push(String(rawNames[index] || '').trim() || `细节${detailUrls.length}`);
  });
  return {
    patternName: String(slot?.patternName || slot?.mainName || '').trim(),
    mainName: String(slot?.mainName || slot?.patternName || '').trim(),
    mainUrl,
    colorCode: String(slot?.colorCode || '').trim(),
    colorLabel: String(slot?.colorLabel || '').trim(),
    detailUrls,
    detailNames,
  };
}

function pickPodSlotsForStep(slotsByGroup, podStep) {
  const groups = (Array.isArray(slotsByGroup) ? slotsByGroup : []).filter((slots) => slots.length > 0);
  if (groups.length === 0) return [];
  if (groups.length === 1) return groups[0];
  const explicit = Number(podStep?.listingGroupIndex);
  if (Number.isInteger(explicit) && explicit >= 0 && explicit < slotsByGroup.length && slotsByGroup[explicit]?.length) {
    return slotsByGroup[explicit];
  }
  const stepUrls = collectStepListingUrls(podStep);
  let best = groups[0];
  let bestScore = 0;
  groups.forEach((slots) => {
    const slotUrls = slots.flatMap((slot) => [slot.mainUrl, ...(slot.detailUrls || [])]);
    const score = slotUrls.filter((url) => stepUrls.some((candidate) => sameListingUrl(candidate, url))).length;
    if (score > bestScore) {
      bestScore = score;
      best = slots;
    }
  });
  return bestScore > 0 ? best : (slotsByGroup[0] || []);
}

async function loadPodColorSlotsFromGoodsTask(podStep) {
  const taskId = String(podStep?.goodsTaskId || podStep?.taskId || '').trim();
  if (!taskId) return [];
  let task = null;
  try {
    const res = await taskAPI.getById(taskId);
    task = res?.task || res?.data || res;
  } catch {
    return [];
  }
  const forms = task?.result?.goodsForms || task?.goodsForms || [];
  if (!Array.isArray(forms) || forms.length === 0) return [];
  let merged = forms;
  try {
    const rows = await fetchAllCompositeImageRows(taskId);
    if (Array.isArray(rows) && rows.length > 0) {
      merged = mergeCompositeImagesIntoGoodsForms(forms, rows);
    }
  } catch {
    merged = forms;
  }
  const groupCount = Math.max(
    1,
    ...merged.map((form) => (Array.isArray(form?.imageGroups) ? form.imageGroups.length : 0)),
  );
  const slotsByGroup = [];
  for (let groupIndex = 0; groupIndex < groupCount; groupIndex += 1) {
    const slots = [];
    merged.forEach((form, formIndex) => {
      slots.push(...buildColorSlotsForGoodsPrintGroup(form, groupIndex, {
        formIndex,
        billingNav: 'podProductDesign',
      }));
    });
    slotsByGroup.push(slots.map(compactWorkflowColorSlot).filter(Boolean));
  }
  return pickPodSlotsForStep(slotsByGroup, podStep);
}

async function collectWorkflowListingAssets(row, result, fallbackUrl) {
  const stamp = findWorkflowResultStep(result, ['stampExtract']);
  const goods = findWorkflowResultStep(result, ['goods']);
  const pod = findWorkflowResultStep(result, ['podProductDesign']);
  const copy = findWorkflowResultStep(result, ['aiCopywriting', 'titles', 'titleExtract']);
  const video = findWorkflowResultStep(result, ['videos', 'textToVideo']);
  const fission = findWorkflowResultStep(result, ['imageFission', 'patternFission']);

  const printUrls = uniqueListingUrls([
    stamp?.outputImageUrl,
    stamp?.stampOutputUrl,
    result?.stampOutputUrl,
    result?.initialImageUrl,
    row?.primaryImageUrl,
    row?.printImageUrl,
  ]);
  const goodsUrls = collectStepListingUrls(goods);
  const podUrls = collectStepListingUrls(pod);
  const fissionUrls = collectStepListingUrls(fission);
  let mainUrls = goodsUrls.length > 0
    ? goodsUrls
    : uniqueListingUrls([result?.finalImageUrl, fallbackUrl]);
  let detailUrls = podUrls.length > 0 ? podUrls : fissionUrls;
  if (mainUrls.length === 0 && podUrls.length > 0) {
    mainUrls = podUrls;
    if (detailUrls.length === 0) detailUrls = [...podUrls];
  }
  if (mainUrls.length === 0) {
    mainUrls = uniqueListingUrls([fallbackUrl]);
  }
  let listingColorSlots = [];
  if (pod) {
    listingColorSlots = overlayGoodsStudioOnColorSlots(
      await loadPodColorSlotsFromGoodsTask(pod),
      goods,
      {
        podStep: pod,
        goodsUrls: collectStepListingUrls(goods),
      },
    );
  }
  if (listingColorSlots.length > 0) {
    mainUrls = listingColorSlots.map((slot) => slot.mainUrl).filter(Boolean);
    detailUrls = listingColorSlots.flatMap((slot) => slot.detailUrls || []);
  }
  const title = pickWorkflowListingTitle(row, result, copy);
  const materialName = pickWorkflowMaterialName(row, printUrls[0] || fallbackUrl);
  const videoUrl = String(
    video?.outputVideoUrl
    || (Array.isArray(video?.outputVideoUrls) ? video.outputVideoUrls[0] : '')
    || result?.outputVideoUrl
    || '',
  ).trim();
  return {
    title,
    materialName,
    printUrls,
    mainUrls,
    detailUrls,
    listingColorSlots,
    videoUrl,
    preferredProductVideo: videoUrl
      ? { url: videoUrl, ipeUrl: videoUrl, videoUrl, name: '工作流视频' }
      : null,
  };
}

async function resolveListingUrlList(urls) {
  const out = [];
  for (const raw of uniqueListingUrls(urls)) {
    try {
      const server = await resolveServerImageUrl(raw);
      if (server && !out.includes(server)) out.push(server);
    } catch {
      if (raw && !out.includes(raw)) out.push(raw);
    }
  }
  return out;
}

/**
 * 等待本地商品库自动规则整段结束（与商品设计→商品刊登相同：预处理 + 上架）。
 * @param {string} batchKey
 * @param {{ timeoutMs?: number, expectRun?: boolean }} [options]
 *   expectRun=true 时，非预期的 skipped 视为失败
 * @returns {Promise<{ status: string, message: string, listingJobId: * }>}
 */
async function waitForListingAutoRule(batchKey, options = {}) {
  const key = String(batchKey || '').trim();
  if (!key) return { status: '', message: '', listingJobId: null };
  const timeoutMs = Number(options.timeoutMs) > 0 ? Number(options.timeoutMs) : 600000;
  const expectRun = options.expectRun === true;
  const started = Date.now();
  let emptySkipRetried = false;
  let emptySkipSince = 0;
  while (Date.now() - started < timeoutMs) {
    // 仅吞掉拉批次的网络抖动；终态业务判定必须在 try 外，避免 throw 被误吞导致死轮询
    let batch = null;
    try {
      batch = await sourcingAPI.getBatch(key);
    } catch {
      await new Promise((resolve) => setTimeout(resolve, 1500));
      continue;
    }
    const status = String(batch?.listingAutoRuleStatus || '').trim();
    const message = String(batch?.listingAutoRuleMessage || '').trim();
    const listingJobId = batch?.listingAutoRuleListingJobId ?? null;
    const captureStatus = String(batch?.captureStatus || '').trim();
    const progressDone = Number(batch?.listingAutoRuleProgressDone) || 0;
    const progressTotal = Number(batch?.listingAutoRuleProgressTotal) || 0;
    // 入库本身失败/被终止：不会再有自动规则调度，快速报错而不是干等
    if (captureStatus === 'terminated') {
      throw new Error(i18n.t('workbench:ipeUtilError.ingestAbortedNoAutoRule'));
    }
    if (status === 'completed' || status === 'failed' || status === 'skipped' || status === 'cancelled') {
      if (status === 'failed') {
        // 刊登任务已落库：视频等预处理失败不应卡住工作流商品库步骤（也不再死轮询）
        if (listingJobId != null) {
          return { status: 'completed', message: message || i18n.t('workbench:ipeUtilError.listingJobCreated'), listingJobId };
        }
        throw new Error(message || i18n.t('workbench:ipeUtilError.autoRuleFailed'));
      }
      if (status === 'skipped' && expectRun) {
        if (/工作流已覆盖|未重复执行|无需重复/.test(message)) {
          return { status, message, listingJobId };
        }
        // 已经有刊登任务：说明上架其实已触发，skipped 只是早前的中间态，不应判失败
        if (listingJobId != null) {
          return { status, message, listingJobId };
        }
        // 空 message：可能是 promote 前中间态，或流水线异常收口成「空 skipped」。
        // 进度已收满(done>=total>0)时几乎是后者——立刻调 retry，再等一小段，仍空则失败。
        if (!message) {
          if (!emptySkipSince) emptySkipSince = Date.now();
          const sealedEmpty = progressTotal > 0 && progressDone >= progressTotal;
          if (sealedEmpty && !emptySkipRetried) {
            emptySkipRetried = true;
            try {
              await sourcingAPI.retryListingAutoRule(key);
            } catch {
              /* 重试失败仍继续轮询，由下方超时/终态处理 */
            }
            await new Promise((resolve) => setTimeout(resolve, 1500));
            continue;
          }
          // 给 claim/流水线最多约 12s；中间态(done 未收满)也可再等
          if (Date.now() - emptySkipSince < (sealedEmpty ? 12000 : 20000)) {
            await new Promise((resolve) => setTimeout(resolve, 1500));
            continue;
          }
          throw new Error(i18n.t('workbench:ipeUtilError.autoRuleEmptySkip', { done: progressDone, total: progressTotal, batch: key }));
        }
        // 带上原始诊断字段，避免只看到「被跳过」却不知原因
        const detail = [
          message,
          `批次 ${key}`,
          `listingAutoRuleStatus=${status || '-'}`,
          `listingAutoRuleStep=${String(batch?.listingAutoRuleStep || '').trim() || '-'}`,
          `captureStatus=${captureStatus || '-'}`,
        ].join('；');
        throw new Error(i18n.t('workbench:ipeUtilError.autoRuleSkipped', { detail }));
      }
      return { status, message, listingJobId };
    }
    emptySkipSince = 0;
    await new Promise((resolve) => setTimeout(resolve, 1500));
  }
  throw new Error(i18n.t('workbench:ipeUtilError.autoRuleTimeout'));
}

/**
 * 入库可能分批写入（部分链路异步）：必须确认条目真正落库再 promote，
 * 否则会出现「写入商品库后未找到条目」而卡在商品库。正常情况下首次调用即返回。
 * @returns {Promise<object[]>}
 */
async function waitForIngestedListingItems(batchKey, expectedCount, timeoutMs = 180000) {
  const expected = Math.max(1, Number(expectedCount) || 1);
  const pageSize = Math.max(50, expected + 10);
  const deadline = Date.now() + timeoutMs;
  let latest = [];
  while (Date.now() < deadline) {
    try {
      const res = await sourcingAPI.listItems({ batchKey, page: 1, pageSize });
      latest = Array.isArray(res?.items) ? res.items : [];
      if (latest.length >= expected) return latest;
    } catch {
      /* 批次仍在写入，继续等 */
    }
    await sleep(1000);
  }
  if (latest.length > 0) return latest;
  throw new Error(i18n.t('workbench:ipeUtilError.ingestWriteTimeout'));
}

/** 工作流「本地商品库」步骤：解析待绑定的自动规则 ID（含 refs 兜底） */
function resolveWorkflowListingAutoRuleIds(params = {}) {
  const requireScheduled = params.requireScheduledListing === true;
  const fromIds = (Array.isArray(params.listingAutoRuleIds) ? params.listingAutoRuleIds : [])
    .map((id) => String(id ?? '').trim())
    .filter(Boolean);
  const fromSingle = params.listingAutoRuleId != null ? String(params.listingAutoRuleId).trim() : '';
  const fromRefs = (Array.isArray(params.listingAutoRuleRefs) ? params.listingAutoRuleRefs : [])
    .map((ref) => String(ref?.id ?? '').trim())
    .filter(Boolean);
  const selectedRuleIds = [...fromIds];
  if (selectedRuleIds.length === 0 && fromSingle) selectedRuleIds.push(fromSingle);
  if (selectedRuleIds.length === 0) selectedRuleIds.push(...fromRefs);
  const uniqueIds = selectedRuleIds.filter((id, idx, arr) => id && arr.indexOf(id) === idx);
  // 与编辑器开关语义对齐：显式关闭则不绑定；否则有规则 ID 即绑定
  const ruleSwitchOff = !requireScheduled && params.useListingAutoRule === false;
  const ruleEnabled = requireScheduled || (!ruleSwitchOff && (
    params.useListingAutoRule === true || uniqueIds.length > 0
  ));
  return ruleEnabled ? uniqueIds : [];
}

/**
 * 工作流已真正产出结果的预处理模块 → 自动规则同名模块跳过，避免二次扣费。
 * 注意：仅看「已成功产出」，不看模板是否搭建（模板有节点但未产出时，自动规则仍应跑）。
 * @returns {string[]} aiCopywriting | videoGeneration | infringement
 */
function detectWorkflowDoneAutoRuleSteps(source) {
  const done = new Set();
  const result = source?.result;
  const steps = Array.isArray(result?.steps) ? result.steps : [];
  const okSteps = steps.filter((step) => {
    const status = String(step?.status || '').trim();
    return status === 'ok' || status === 'stopped';
  });

  // AI 文案：必须是 aiCopywriting 且已有文案正文（titles/titleExtract 不算，避免误跳过）
  const copyStep = okSteps.find((step) => String(step?.navId || '').trim() === 'aiCopywriting');
  const copyText = String(
    copyStep?.copywritingText || result?.copywritingText || '',
  ).trim();
  if (copyStep && copyText) done.add('aiCopywriting');

  // 视频：图生/文生视频成功且有成片 URL
  const videoStep = okSteps.find((step) => {
    const nav = String(step?.navId || '').trim();
    return nav === 'videos' || nav === 'textToVideo';
  });
  const videoUrl = String(
    videoStep?.outputVideoUrl
    || (Array.isArray(videoStep?.outputVideoUrls) ? videoStep.outputVideoUrls[0] : '')
    || result?.outputVideoUrl
    || '',
  ).trim();
  if (videoStep && videoUrl) done.add('videoGeneration');

  // 侵权：已发起检测（含高危 stopped）
  const infrStep = okSteps.find((step) => String(step?.navId || '').trim() === 'infringementDetection');
  const infrId = infrStep?.infringementBatchId || infrStep?.batchId || result?.infringementBatchId;
  if (infrStep && infrId != null && String(infrId).trim() !== '') {
    done.add('infringement');
  }
  return done;
}

/**
 * 多条 source 合并入库时：仅当组内每条都已产出该模块，才跳过（宁可多跑，不可误跳过）。
 */
function resolveWorkflowAutoRuleSkipSteps(sources = []) {
  const list = (Array.isArray(sources) ? sources : []).filter(Boolean);
  if (list.length === 0) return [];
  const perSource = list.map((source) => detectWorkflowDoneAutoRuleSteps(source));
  return ['aiCopywriting', 'videoGeneration', 'infringement'].filter((mod) => (
    perSource.every((set) => set.has(mod))
  ));
}

/** 同一 POD 印花组（正/背多张素材）合并为一条商品库 SPU */
function resolveWorkflowListingMergeKey(source) {
  const pod = findWorkflowResultStep(source?.result, ['podProductDesign']);
  const taskId = String(pod?.goodsTaskId || pod?.taskId || '').trim();
  const groupIndex = Number(pod?.listingGroupIndex);
  if (taskId && Number.isInteger(groupIndex) && groupIndex >= 0) {
    return `pod:${taskId}:g:${groupIndex}`;
  }
  if (taskId) return `pod:${taskId}:g:0`;
  return `row:${source?.row?.id ?? ''}`;
}

function groupSourcesForListingIngest(sources = []) {
  const orderedKeys = [];
  const map = new Map();
  (Array.isArray(sources) ? sources : []).forEach((source) => {
    if (!source?.url) return;
    const key = resolveWorkflowListingMergeKey(source);
    if (!map.has(key)) {
      orderedKeys.push(key);
      map.set(key, []);
    }
    map.get(key).push(source);
  });
  return orderedKeys.map((key) => map.get(key)).filter((list) => list.length > 0);
}

async function buildWorkflowListingIngestItem(groupSources = [], {
  bindAutoRule = false,
  workflowRemark = '',
  skipAutoRuleSteps = [],
} = {}) {
  const list = (Array.isArray(groupSources) ? groupSources : []).filter((s) => s?.url);
  if (list.length === 0) return null;
  const primarySource = list[0];

  const printUrls = [];
  const printNames = [];
  let listingColorSlots = [];
  let preferredProductVideo = null;
  let itemTitle = '';
  const rowIds = [];
  const fallbackMainUrls = [];
  const fallbackDetailUrls = [];

  for (const source of list) {
    rowIds.push(source.row?.id ?? null);
    const assets = await collectWorkflowListingAssets(source.row, source.result, source.url);
    if (!itemTitle) itemTitle = assets.title || '';
    if (!preferredProductVideo && assets.preferredProductVideo) {
      preferredProductVideo = assets.preferredProductVideo;
    }
    if (listingColorSlots.length === 0 && Array.isArray(assets.listingColorSlots)) {
      listingColorSlots = assets.listingColorSlots;
    }
    fallbackMainUrls.push(...(assets.mainUrls || []));
    fallbackDetailUrls.push(...(assets.detailUrls || []));
    const materialName = assets.materialName
      || pickWorkflowMaterialName(source.row, source.url);
    const urls = await resolveListingUrlList(assets.printUrls);
    urls.forEach((url) => {
      if (!url || printUrls.some((existing) => sameListingUrl(existing, url))) return;
      printUrls.push(url);
      printNames.push(resolveListingImageName(
        [materialName, fileNameFromListingUrl(url)],
        `印花${printUrls.length}`,
      ));
    });
  }

  itemTitle = itemTitle || String(primarySource.row?.id || '工作流商品').slice(0, 200);
  const primaryMaterialName = pickWorkflowMaterialName(primarySource.row, printUrls[0] || primarySource.url);

  let colorSlots = [];
  if (Array.isArray(listingColorSlots) && listingColorSlots.length > 0) {
    for (const slot of listingColorSlots) {
      const mainUrl = (await resolveListingUrlList([slot.mainUrl]))[0] || '';
      if (!mainUrl) continue;
      const detailUrls = [];
      const detailNames = [];
      const rawDetails = Array.isArray(slot.detailUrls) ? slot.detailUrls : [];
      for (let index = 0; index < rawDetails.length; index += 1) {
        const url = (await resolveListingUrlList([rawDetails[index]]))[0] || '';
        if (!url || sameListingUrl(url, mainUrl)) continue;
        if (detailUrls.some((existing) => sameListingUrl(existing, url))) continue;
        detailUrls.push(url);
        detailNames.push(resolveListingImageName(
          [slot.detailNames?.[index], primaryMaterialName, fileNameFromListingUrl(url)],
          `细节${detailUrls.length}`,
        ));
      }
      const mainName = resolveListingImageName(
        [slot.mainName, slot.patternName, primaryMaterialName, fileNameFromListingUrl(mainUrl)],
        `主图${colorSlots.length + 1}`,
      );
      colorSlots.push({
        patternName: mainName,
        mainName,
        mainUrl,
        colorCode: slot.colorCode || '',
        colorLabel: slot.colorLabel || '',
        detailUrls,
        detailNames,
      });
    }
    const slotMains = colorSlots.map((slot) => slot.mainUrl);
    colorSlots = colorSlots.map((slot) => {
      const detailUrls = [];
      const detailNames = [];
      slot.detailUrls.forEach((url, index) => {
        if (slotMains.some((main) => sameListingUrl(main, url))) return;
        detailUrls.push(url);
        detailNames.push(resolveListingImageName(
          [slot.detailNames[index], primaryMaterialName, fileNameFromListingUrl(url)],
          `细节${detailUrls.length}`,
        ));
      });
      return { ...slot, detailUrls, detailNames };
    });
  }

  const mainUrls = colorSlots.length > 0
    ? colorSlots.map((slot) => slot.mainUrl)
    : await resolveListingUrlList(fallbackMainUrls);
  const detailUrls = colorSlots.length > 0
    ? colorSlots.flatMap((slot) => slot.detailUrls)
    : await resolveListingUrlList(fallbackDetailUrls);
  const primary = mainUrls[0] || await resolveServerImageUrl(primarySource.url);
  if (colorSlots.length === 0 && primary) {
    const mainName = resolveListingImageName(
      [primaryMaterialName, fileNameFromListingUrl(primary)],
      '主图1',
    );
    colorSlots = [{
      patternName: mainName,
      mainName,
      mainUrl: primary,
      colorCode: '',
      colorLabel: '',
      detailUrls,
      detailNames: detailUrls.map((url, index) => resolveListingImageName(
        [primaryMaterialName, fileNameFromListingUrl(url)],
        `细节${index + 1}`,
      )),
    }];
  }
  const mainImageNames = colorSlots.length > 0
    ? colorSlots.map((slot, index) => resolveListingImageName(
      [slot.mainName, primaryMaterialName, fileNameFromListingUrl(slot.mainUrl)],
      `主图${index + 1}`,
    ))
    : mainUrls.map((url, index) => resolveListingImageName(
      [primaryMaterialName, fileNameFromListingUrl(url)],
      `主图${index + 1}`,
    ));
  const detailImageNames = colorSlots.length > 0
    ? colorSlots.flatMap((slot) => slot.detailNames)
    : detailUrls.map((url, index) => resolveListingImageName(
      [primaryMaterialName, fileNameFromListingUrl(url)],
      `细节${index + 1}`,
    ));

  return {
    externalKey: `wf-listing-${rowIds.filter(Boolean).join('-') || 'row'}-${Date.now()}`,
    title: itemTitle,
    primaryImageUrl: primary,
    imageUrls: mainUrls.length > 0 ? mainUrls : [primary],
    listingText: workflowRemark
      ? `工作流入库 · 条目 ${rowIds.filter((id) => id != null).join(',') || ''} · ${workflowRemark}`
      : `工作流入库 · 条目 ${rowIds.filter((id) => id != null).join(',') || ''}`,
    raw: {
      source: 'workflow_platform_listing',
      ...(bindAutoRule ? {} : { skipListingAutoRule: true }),
      ...(Array.isArray(skipAutoRuleSteps) && skipAutoRuleSteps.length > 0
        ? { skipAutoRuleSteps: [...skipAutoRuleSteps] }
        : {}),
      rowId: primarySource.row?.id ?? null,
      rowIds: rowIds.filter((id) => id != null),
      workflowRemark: workflowRemark || null,
      printImageUrls: printUrls,
      printImageNames: printNames,
      detailImageUrls: detailUrls,
      detailImageNames,
      mainImageNames,
      colorSlots,
      ...(preferredProductVideo ? { preferredProductVideo } : {}),
      imageRoles: {
        primary: mainUrls.length > 1 ? 'goods_composite' : 'pipeline_current',
        detail: detailUrls.length > 0 ? 'pod_or_fission' : 'none',
        print: printUrls.length > 0 ? 'stamp_or_source' : 'none',
        title: 'ai_copywriting_or_row',
      },
    },
  };
}

/**
 * 将流水线产出写入本地商品库；绑定自动规则时与「商品设计→商品刊登」相同：
 * promote 后由后端跑完整自动规则（AI/视频/侵权/上架），前端只补 TEMU 转换与额外规则。
 */
async function runPlatformListingBatchStep(sources = [], params = {}, workflowRemark = '', options = {}) {
  const validSources = (Array.isArray(sources) ? sources : []).filter((s) => s && s.url);
  if (validSources.length === 0) throw new Error(i18n.t('workbench:ipeUtilError.goodsLibMissingInput'));

  const requireScheduled = params.requireScheduledListing === true;
  const wantAutoPublish = params.autoCreateListingJob !== false || requireScheduled;
  const selectedRuleIds = resolveWorkflowListingAutoRuleIds(params);
  const primaryRuleId = selectedRuleIds[0] || null;
  const bindAutoRule = wantAutoPublish && Boolean(primaryRuleId);
  const ruleIdNum = bindAutoRule ? Number(primaryRuleId) : NaN;
  if (bindAutoRule && !Number.isFinite(ruleIdNum)) {
    throw new Error(i18n.t('workbench:ipeUtilError.batchPublishRuleIdInvalid'));
  }
  if (requireScheduled && selectedRuleIds.length === 0) {
    throw new Error(i18n.t('workbench:ipeUtilError.scheduledPublishNeedRule'));
  }

  const ingestGroups = groupSourcesForListingIngest(validSources);
  const ingestItems = [];
  for (const group of ingestGroups) {
    const skipAutoRuleSteps = resolveWorkflowAutoRuleSkipSteps(group);
    const item = await buildWorkflowListingIngestItem(group, {
      bindAutoRule,
      workflowRemark,
      skipAutoRuleSteps,
    });
    if (item) ingestItems.push(item);
  }

  const title = String(validSources[0].row?.title || validSources[0].row?.id || '工作流商品')
    .trim().slice(0, 200) || '工作流商品';
  if (ingestItems.length === 0) {
    throw new Error(i18n.t('workbench:ipeUtilError.goodsLibNoIngestItems'));
  }
  const ingestRes = await sourcingAPI.ingestBatch({
    platformKey: 'ipe-workflow-listing',
    pageUrl: typeof window !== 'undefined' ? window.location.href : '',
    pageTitle: `工作流批量上架 · ${title}`,
    sourceSite: 'IPE',
    pluginVersion: 'workflow-pipeline',
    finalize: true,
    expectedItemCount: ingestItems.length,
    items: ingestItems,
    ...(bindAutoRule
      ? { bindListingAutoRule: true, listingAutoRuleId: ruleIdNum }
      : {}),
  });
  const batchKey = ingestRes?.batch?.batchKey;
  if (!batchKey) throw new Error(i18n.t('workbench:ipeUtilError.ingestNoBatchReturned'));

  const itemsRes = await waitForIngestedListingItems(batchKey, ingestItems.length);
  const listingItems = itemsRes;
  const sourcingItemIds = listingItems
    .map((it) => it?.id)
    .filter((id) => id != null);
  if (sourcingItemIds.length === 0) throw new Error(i18n.t('workbench:ipeUtilError.ingestNoItemsFound'));

  await sourcingAPI.promoteItems(sourcingItemIds, {
    ...(bindAutoRule
      ? { bindListingAutoRule: true, listingAutoRuleId: ruleIdNum }
      : {}),
  });

  const listingRuleRefs = Array.isArray(params.listingAutoRuleRefs) ? params.listingAutoRuleRefs : [];
  const listingAutoRuleNames = uniqueListingLabels([
    ...listingRuleRefs.map((ref) => ref?.name),
    ...(await Promise.all(selectedRuleIds.map(async (id) => {
      const rule = await fetchListingAutoRuleById(id);
      return rule?.name;
    }))),
  ]);
  const listingNote = formatListingLibraryNote({
    count: sourcingItemIds.length,
    batchKey,
    ruleNames: listingAutoRuleNames,
    associated: selectedRuleIds.length > 0,
  });

  if (!bindAutoRule) {
    const ruleSwitchOff = !requireScheduled && params.useListingAutoRule === false;
    if (wantAutoPublish && !ruleSwitchOff) {
      throw new Error(i18n.t('workbench:ipeUtilError.localGoodsNoAutoRule'));
    }
    return {
      batchKey,
      sourcingItemIds,
      listingAutoRuleNames,
      note: listingNote,
    };
  }

  const settled = await waitForListingAutoRule(batchKey, { expectRun: true });
  const remark = workflowRemark
    ? `工作流批量上架 · 条目 ${validSources[0].row?.id ?? ''} 等 ${validSources.length} 条 · ${workflowRemark}`
    : `工作流批量上架 · 条目 ${validSources[0].row?.id ?? ''} 等 ${validSources.length} 条`;

  // 与商品设计→商品刊登一致：首条规则由后端流水线负责；TEMU/TikTok 补转换，多规则补额外上架
  const { publishExtraAutoRulesAfterIngest } = await import(
    '@/pages/ipe/workbench/utils/publishItemsWithListingAutoRules'
  );
  await publishExtraAutoRulesAfterIngest({
    items: listingItems.filter((row) => sourcingItemIds.includes(row?.id)),
    sourcingItemIds,
    listingAutoRuleIds: selectedRuleIds,
    primaryRuleId,
    remarkPrefix: remark,
    batchKey,
  });

  const batchAfter = await sourcingAPI.getBatch(batchKey).catch(() => null);
  const listingJobId = batchAfter?.listingAutoRuleListingJobId ?? settled.listingJobId ?? null;
  const message = String(batchAfter?.listingAutoRuleMessage || settled.message || '').trim();

  return {
    batchKey,
    sourcingItemIds,
    listingJobId,
    shopName: '',
    listingAutoRuleNames,
    note: message || listingNote,
  };
}

/** 单条目兼容入口：非「模块优先」的模板仍按条目逐个执行。 */
async function runPlatformListingStep(
  row,
  currentUrl,
  params = {},
  workflowRemark = '',
  result = null,
  options = {},
) {
  return runPlatformListingBatchStep(
    [{ row, url: currentUrl, result }],
    params,
    workflowRemark,
    options,
  );
}

/* ---------------------------------------------------------------------------
 * 阶段（模块）批量执行：一个工作流任务在同一模块只创建一个批次任务
 * ------------------------------------------------------------------------- */

async function persistBatchOutputs(navId, sources, buckets, label) {
  const bySource = [];
  for (let i = 0; i < sources.length; i += 1) {
    const list = buckets[i] || [];
    if (list.length === 0) {
      bySource.push({ rowId: sources[i].rowId, failed: true, error: i18n.t('workbench:ipeUtilError.rowNoImageOutput') });
      continue;
    }
    const saved = await persistProcessedImageToSpace(
      `wf-batch-${navId}-${sources[i].rowId ?? i}`,
      list[0],
      label,
      { navId },
    );
    bySource.push({
      rowId: sources[i].rowId,
      ...attachWorkflowImagePreview(saved, list),
    });
  }
  return bySource;
}

/** 图案裁剪批量：一个任务提交 N 张图，worker 逐张裁剪。 */
async function runCropBatchStep(sources, workflowContext = {}) {
  const resolved = [];
  for (const source of sources) {
    const apiUrl = await resolveImg2ImgReferenceForApi(source.url);
    if (!apiUrl) throw new Error(i18n.t('workbench:ipeUtilError.imageCropSourceUnresolved'));
    resolved.push({ ...source, apiUrl });
  }
  const { taskId, initialResult } = await beginWorkflowFeatureTask({
    taskType: 'imageCrop',
    taskName: '图案裁剪',
    imageIds: resolved.map((s) => s.spaceId).filter(Boolean),
    previewUrl: resolved[0]?.url || '',
    estimatedTotal: resolved.length,
    workflowContext,
    initialResult: {
      engine: 'qwen-detect',
      cropMode: IMAGE_CROP_WORKER_MODE,
      referenceUrls: resolved.map((s) => s.apiUrl),
      serverWorker: true,
      batchPhase: 'queued',
      outputs: [],
      urls: [],
    },
  });
  try {
    await submitImageCropTask({
      taskId,
      images: resolved.map((s) => ({ imageId: s.spaceId, imageUrl: s.apiUrl })),
      mode: IMAGE_CROP_WORKER_MODE,
    });
    const task = await waitForFundingTaskComplete(taskId);
    const status = String(task?.status || '').toLowerCase();
    if (status === 'failed' || status === 'canceled' || status === 'cancelled') {
      throw new Error(task?.result?.failureHint || i18n.t('workbench:ipeUtilError.imageCropFailed'));
    }
    const buckets = groupOutputUrlsBySource(task?.result || {}, resolved.length, 1);
    if (!buckets.some((list) => list.length > 0)) {
      // 回退：裁剪批次结果按 imageId 对齐
      const batch = await pollImageCropBatch(taskId, 1);
      const rows = Array.isArray(batch?.images) ? batch.images : [];
      rows.forEach((row) => {
        const url = pickCroppedUrlFromRow(row);
        if (!url) return;
        const id = String(row?.imageId || row?.id || '');
        const idx = resolved.findIndex((s) => String(s.spaceId) === id);
        if (idx >= 0) buckets[idx].push(url);
      });
    }
    const bySource = await persistBatchOutputs('imageCrop', sources, buckets, '工作流-裁剪');
    const allUrls = buckets.flat();
    await completeWorkflowFeatureImageTask(taskId, allUrls, resolved.length, initialResult, {
      note: '已在工作流中完成（图案裁剪）',
    }, task?.result || {}, 'imageCrop');
    return { taskId, bySource };
  } catch (error) {
    await failWorkflowFeatureTask(taskId, initialResult, resolved.length, error);
    throw error;
  }
}

/** 超分提质批量：一个任务提交 N 张图，worker 逐张放大。 */
async function runEnhanceBatchStep(sources, workflowContext = {}) {
  const resolved = [];
  for (const source of sources) {
    const apiUrl = await resolveImg2ImgReferenceForApi(source.url);
    if (!apiUrl) throw new Error(i18n.t('workbench:ipeUtilError.enhanceSourceUnresolved'));
    resolved.push({ ...source, apiUrl });
  }
  try {
    await bumpImageSpaceUsageCounts(resolved.map((s) => String(s.spaceId)).filter(Boolean));
  } catch (e) {
    console.warn('[workflow 超分提质] 使用次数更新失败', e);
  }
  const { taskId, initialResult } = await beginWorkflowFeatureTask({
    taskType: 'imageEnhance',
    taskName: '超分提质',
    imageIds: resolved.map((s) => s.spaceId).filter(Boolean),
    previewUrl: resolved[0]?.url || '',
    estimatedTotal: resolved.length,
    workflowContext,
    initialResult: {
      engine: 'qwen-image-2.0',
      pipeline: 'qwen-clarity-v1',
      referenceUrls: resolved.map((s) => s.apiUrl),
      localEnhanceUrls: {},
      localFailedImageIds: [],
      serverWorker: true,
      batchPhase: 'queued',
      outputs: [],
      urls: [],
    },
  });
  try {
    await taskAPI.submit(taskId);
    const task = await waitForFundingTaskComplete(taskId);
    const status = String(task?.status || '').toLowerCase();
    if (status === 'failed' || status === 'canceled' || status === 'cancelled') {
      throw new Error(task?.result?.failureHint || i18n.t('workbench:ipeUtilError.enhanceFailed'));
    }
    const buckets = groupOutputUrlsBySource(task?.result || {}, resolved.length, 1);
    const bySource = await persistBatchOutputs('imageEnhance', sources, buckets, '工作流-放大');
    const allUrls = buckets.flat();
    await completeWorkflowFeatureImageTask(taskId, allUrls, resolved.length, initialResult, {
      note: '已在工作流中完成（超分提质）',
    }, task?.result || {}, 'imageEnhance');
    return { taskId, bySource };
  } catch (error) {
    await failWorkflowFeatureTask(taskId, initialResult, resolved.length, error);
    throw error;
  }
}

/** 印花图提取批量：一个任务提交 N 张图。 */
async function runStampExtractBatchStep(sources, workflowContext = {}) {
  const resolved = [];
  for (const source of sources) {
    const apiUrl = await resolveImg2ImgReferenceForApi(source.url);
    if (!apiUrl) throw new Error(i18n.t('workbench:ipeUtilError.stampExtractOrigUnresolved'));
    resolved.push({ ...source, apiUrl });
  }
  const { taskId, initialResult } = await beginWorkflowFeatureTask({
    taskType: 'stampExtract',
    taskName: '印花提取',
    imageIds: resolved.map((s) => s.spaceId).filter(Boolean),
    previewUrl: resolved[0]?.url || '',
    estimatedTotal: resolved.length,
    workflowContext,
    initialResult: {
      engine: 'qwen-image-2.0',
      referenceUrls: resolved.map((s) => s.apiUrl),
      serverWorker: true,
      batchPhase: 'queued',
      outputs: [],
      urls: [],
    },
  });
  try {
    await taskAPI.submit(taskId);
    const task = await waitForFundingTaskComplete(taskId);
    const status = String(task?.status || '').toLowerCase();
    if (status === 'failed' || status === 'canceled' || status === 'cancelled') {
      throw new Error(task?.result?.failureHint || i18n.t('workbench:ipeUtilError.stampExtractFailed'));
    }
    const buckets = groupOutputUrlsBySource(task?.result || {}, resolved.length, 1);
    if (!buckets.some((list) => list.length > 0)) {
      const one = extractStampExtractResultUrl(task?.result || {});
      if (one) buckets[0].push(one);
    }
    const bySource = [];
    for (let i = 0; i < resolved.length; i += 1) {
      const list = buckets[i] || [];
      if (list.length === 0) {
        bySource.push({ rowId: resolved[i].rowId, failed: true, error: i18n.t('workbench:ipeUtilError.rowNoStampExtracted') });
        continue;
      }
      const stampName = String(resolved[i]?.name || resolved[i]?.title || '').trim() || '工作流-印花提取';
      const saved = await persistProcessedImageToSpace(
        `wf-stamp-${resolved[i].rowId ?? i}`,
        list[0],
        stampName,
        { navId: 'stampExtract' },
      );
      bySource.push({
        rowId: resolved[i].rowId,
        ...attachWorkflowImagePreview(saved, list),
      });
    }
    await completeWorkflowFeatureImageTask(taskId, buckets.flat(), resolved.length, initialResult, {
      note: '插件工作流：服务端队列提取印花',
    }, task?.result || {}, 'stampExtract');
    return { taskId, bySource };
  } catch (error) {
    await failWorkflowFeatureTask(taskId, initialResult, resolved.length, error);
    throw error;
  }
}

/** 智能抠图批量：一个任务承载 N 张图（客户端逐张执行，任务只建一个）。 */
async function runCutoutBatchStep(sources, params = {}, workflowContext = {}) {
  const firstSource = sources[0];
  const cutoutMode = String(params?.cutoutMode || params?.mode || 'basic').toLowerCase() === 'advanced'
    ? 'advanced'
    : 'basic';
  const { taskId, initialResult } = await beginWorkflowFeatureTask({
    taskType: 'smartCutout',
    taskName: '智能抠图',
    imageIds: sources.map((s) => s.spaceId).filter(Boolean),
    previewUrl: firstSource?.url || '',
    estimatedTotal: sources.length,
    workflowContext,
    initialResult: {
      cutoutMode,
      cutoutEngine: cutoutMode === 'advanced' ? 'qwen' : 'aliyun',
      engine: 'ipe-cutout',
      cutoutFormats: ['png'],
      outputFormat: 'png',
      serverWorker: false,
    },
  });
  const buckets = sources.map(() => []);
  const bySource = sources.map((s) => ({
    rowId: s.rowId,
    taskId,
    spaceId: null,
    imageUrl: null,
    compositeImageUrls: [],
  }));
  try {
    for (let i = 0; i < sources.length; i += 1) {
      const source = sources[i];
      const imgForApi = await toProcessableImageUrl(source.url);
      if (!imgForApi) throw new Error(i18n.t('workbench:ipeUtilError.cutoutSourceUnresolved'));
      const subAccount = String(workflowContext?.workflowTaskId || source.rowId || 'wf').slice(0, 40);
      const cutoutResult = await runSmartCutoutProcess(
        subAccount,
        { id: source.rowId },
        imgForApi,
        { formats: ['png'], mode: cutoutMode },
      );
      const outputUrl = cutoutResult?.url || extractStampExtractResultUrl(cutoutResult?.rawResult);
      if (!outputUrl || !String(outputUrl).trim()) throw new Error(i18n.t('workbench:ipeUtilError.cutoutNoOutput'));
      const dims = await imageNaturalSize(outputUrl);
      await persistSmartCutoutModuleImageSpace(taskId, source.spaceId, outputUrl, {
        width: dims.width || undefined,
        height: dims.height || undefined,
      });
      const saved = await persistProcessedImageToSpace(
        `wf-cut-${source.rowId ?? i}`,
        outputUrl,
        '工作流-抠图',
        { navId: 'smartCutout' },
      );
      buckets[i].push(saved.imageUrl);
      Object.assign(bySource[i], attachWorkflowImagePreview(saved, [saved.imageUrl]));
    }
    const allUrls = buckets.flat();
    await completeWorkflowFeatureImageTask(taskId, allUrls, sources.length, initialResult, {
      cutoutEngine: 'ipe',
      cutoutFormats: ['png'],
      outputFormat: 'png',
      note: '已在工作流中完成（智能抠图）',
      engine: 'ipe-cutout-v2',
    });
    return { taskId, bySource };
  } catch (error) {
    await failWorkflowFeatureTask(taskId, initialResult, sources.length, error);
    throw error;
  }
}

/** 侵权检测批量：一个检测批次承载全部条目图片。 */
async function runInfringementBatchStep(sources, params = {}, interactive = {}, workflowContext = {}) {
  const mode = params.filterMode === 'deep' ? 'deep' : 'basic';
  const imageUrls = [];
  for (const source of sources) {
    imageUrls.push(await resolveServerImageUrl(source.url));
  }
  const created = await infringementAPI.createBatch({
    imageUrls,
    mode,
    remark: `工作流 · ${sources.length} 个条目`.trim(),
  });
  const batchId = created?.id;
  if (!batchId) throw new Error(i18n.t('workbench:ipeUtilError.infringementCreateFailed'));
  const detail = await pollInfringementBatch(batchId);
  const items = Array.isArray(detail?.items) ? detail.items : [];
  const buckets = sources.map(() => []);
  items.forEach((item, idx) => {
    const itemUrl = String(item?.imageUrl || item?.url || '').trim();
    let si = -1;
    if (itemUrl) si = imageUrls.findIndex((u) => u === itemUrl);
    if (si < 0) si = idx;
    if (si >= 0 && si < buckets.length) buckets[si].push(item);
  });

  const bySource = [];
  for (let i = 0; i < sources.length; i += 1) {
    const source = sources[i];
    const sourceItems = buckets[i];
    const counts = { high: 0, medium: 0, low: 0, unknown: 0 };
    sourceItems.forEach((it) => {
      const r = String(it?.riskLevel || 'unknown').toLowerCase();
      if (r === 'high') counts.high += 1;
      else if (r === 'medium') counts.medium += 1;
      else if (r === 'low') counts.low += 1;
      else counts.unknown += 1;
    });
    const topRisk = sourceItems[0]?.riskLevel || 'unknown';
    const advice = sourceItems[0]?.advice || '';
    const highRisk = isHighInfringementRisk(counts, topRisk);
    const noteParts = [
      `批次 ${detail?.batchNo || batchId}`,
      `风险：高 ${counts.high} / 中 ${counts.medium} / 低 ${counts.low}`,
    ];
    if (advice) noteParts.push(advice);

    if (highRisk && typeof interactive.onRiskPrompt === 'function') {
      const decision = await interactive.onRiskPrompt({
        source,
        detail,
        items: sourceItems,
        counts,
        advice,
        batchId,
        batchNo: detail?.batchNo,
        topRisk,
      });
      if (decision === 'skip' || decision === 'stopItem' || decision === 'terminate') {
        bySource.push({
          rowId: source.rowId,
          batchId,
          batchNo: detail?.batchNo,
          riskLevel: topRisk,
          highRisk: true,
          note: noteParts.join(' · '),
          skipped: true,
        });
        continue;
      }
      noteParts.push('（用户选择继续后续流程）');
    } else if (params.blockOnHighRisk !== false && highRisk) {
      bySource.push({
        rowId: source.rowId,
        batchId,
        batchNo: detail?.batchNo,
        riskLevel: topRisk,
        highRisk: true,
        note: noteParts.join(' · '),
        failed: true,
        error: advice
          || `检测到高风险图片（${counts.high} 张），已中止后续步骤。可在「侵权检测」查看批次 ${detail?.batchNo || batchId}`,
      });
      continue;
    }

    bySource.push({
      rowId: source.rowId,
      batchId,
      batchNo: detail?.batchNo,
      riskLevel: topRisk,
      highRisk,
      note: noteParts.join(' · '),
    });
  }
  return { batchId, batchNo: detail?.batchNo, taskId: null, bySource };
}

function buildPipelineInputSnapshot(out, currentSpaceId, currentUrl, stampOutputSpaceId, stampOutputUrl) {
  return {
    currentSpaceId,
    currentUrl,
    stampOutputSpaceId,
    stampOutputUrl,
    initialImageUrl: out.initialImageUrl,
  };
}

function mergeBranchPipelineOutput(out, template, branchResults) {
  const results = (branchResults || []).filter(Boolean);
  const ordered = [];
  results.forEach((br) => {
    (br.steps || []).forEach((rec) => {
      ordered.push({
        rec,
        order: resolveWorkflowStepFlatIndex(template, rec),
      });
    });
  });
  ordered.sort((a, b) => a.order - b.order);
  out.steps = ordered.map((item) => item.rec);

  let anyOk = false;
  const errors = [];

  results.forEach((br) => {
    if (br?.ok !== false || (br?.steps || []).some((s) => s?.status === 'ok')) {
      anyOk = true;
    }
    if (br?.titleTaskId) out.titleTaskId = br.titleTaskId;
    if (br?.titleText) out.titleText = br.titleText;
    if (br?.copywritingTaskId) out.copywritingTaskId = br.copywritingTaskId;
    if (br?.copywritingText) out.copywritingText = br.copywritingText;
    if (br?.copywritingLanguage) out.copywritingLanguage = br.copywritingLanguage;
    if (br?.infringementBatchId) out.infringementBatchId = br.infringementBatchId;
    if (br?.infringementHighRisk) {
      out.infringementHighRisk = true;
      out.infringementRiskLevel = br.infringementRiskLevel;
      out.infringementNote = br.infringementNote;
    }
    if (br?.listingJobId) out.listingJobId = br.listingJobId;
    if (br?.sourcingItemIds) out.sourcingItemIds = br.sourcingItemIds;
    if (br?.finalImageUrl) {
      out.finalImageUrl = br.finalImageUrl;
      out.finalSpaceId = br.finalSpaceId;
    }
    if (br?.skipped) {
      out.skipped = true;
      out.stoppedAtNavId = br.stoppedAtNavId;
      if (br.error) errors.push(br.error);
    }
    if (br?.pendingReview) {
      out.pendingReview = true;
      out.reviewAtStepIndex = br.reviewAtStepIndex;
      out.reviewStepLabel = br.reviewStepLabel;
      out.reviewNavId = br.reviewNavId;
    }
    if (br?.ok === false && br?.error) {
      errors.push(br.error);
    }
  });

  if (out.pendingReview) {
    out.ok = false;
  } else if (out.skipped) {
    out.ok = false;
    out.error = errors[0] || out.error || '已终止当前条目';
  } else if (!anyOk && errors.length > 0) {
    out.ok = false;
    out.error = errors.join('；') || '分支执行失败';
  } else if (errors.length > 0) {
    out.ok = false;
    out.error = errors.join('；');
  } else {
    out.ok = true;
  }

  return out;
}

async function settleBranchExecutions(branches, runner) {
  const settled = await Promise.allSettled(branches.map((branchSteps) => runner(branchSteps)));
  const fatal = settled.find(
    (entry) =>
      entry.status === 'rejected'
      && (entry.reason?.batchTerminated
        || entry.reason?.name === 'WorkflowBatchTerminatedError'
        || entry.reason?.name === 'WorkflowTaskAbortedError'
        || entry.reason?.message === '任务已终止'),
  );
  if (fatal) throw fatal.reason;
  return settled.map((entry, index) => {
    if (entry.status === 'fulfilled') {
      return entry.value || { ok: false, error: i18n.t('workbench:ipeUtilError.branchNoResult'), steps: [] };
    }
    const reason = entry.reason;
    const message = reason?.message || String(reason || '分支执行异常');
    console.error('[工作流分支执行] 分支失败:', branches[index]?.map?.((s) => s.label).join(' → '), message);
    return { ok: false, error: message, steps: [] };
  });
}

async function runBranchPipelinePlan(row, template, plan, options) {
  const {
    onStep,
    onInfringementRiskPrompt,
    onGoodsStampAssignPrompt,
    rowIndex,
    workflowRemark,
    workflowRunId,
    workflowContextInput,
    shouldAbort,
    inputSnap,
  } = options;

  const runBranchSteps = async (branchSteps) => {
    if (!branchSteps?.length) {
      return { ok: false, error: i18n.t('workbench:ipeUtilError.emptyBranch'), steps: [] };
    }
    try {
      return await runPipelineForRow(
        row,
        buildBranchExecutionTemplate(template, branchSteps),
        {
          onStep: (ev) => {
            const step = branchSteps[ev.index ?? 0] ?? branchSteps[0];
            const flatIndex = resolveWorkflowStepFlatIndex(template, step);
            onStep?.({ ...ev, index: flatIndex >= 0 ? flatIndex : ev.index });
          },
          onInfringementRiskPrompt,
          onGoodsStampAssignPrompt,
          rowIndex,
          workflowRemark,
          workflowRunId,
          workflowContext: workflowContextInput,
          shouldAbort,
          initialPipelineState: inputSnap,
          skipStepDelay: branchSteps.length > 1,
        },
      );
    } catch (error) {
      if (isFatalPipelineError(error)) {
        throw error;
      }
      return {
        ok: false,
        error: error?.message || String(error),
        steps: [],
      };
    }
  };

  if (plan.mode === 'join') {
    const prefixOuts = await settleBranchExecutions(plan.branches, runBranchSteps);
    if (prefixOuts.some((br) => br?.skipped || br?.pendingReview)) {
      return { branchResults: prefixOuts, suffixOut: null };
    }
    let mergedSnap = { ...inputSnap };
    prefixOuts.forEach((br) => {
      if (br?.finalImageUrl) {
        mergedSnap = {
          ...mergedSnap,
          currentUrl: br.finalImageUrl,
          currentSpaceId: br.finalSpaceId,
          stampOutputSpaceId: mergedSnap.stampOutputSpaceId,
          stampOutputUrl: mergedSnap.stampOutputUrl,
        };
      }
    });
    let suffixOut = null;
    try {
      suffixOut = await runPipelineForRow(
        row,
        buildBranchExecutionTemplate(template, plan.suffix),
        {
          onStep: (ev) => {
            const step = plan.suffix[ev.index ?? 0] ?? plan.suffix[0];
            const flatIndex = resolveWorkflowStepFlatIndex(template, step);
            onStep?.({ ...ev, index: flatIndex >= 0 ? flatIndex : ev.index });
          },
          onInfringementRiskPrompt,
          onGoodsStampAssignPrompt,
          rowIndex,
          workflowRemark,
          workflowRunId,
          workflowContext: workflowContextInput,
          shouldAbort,
          initialPipelineState: mergedSnap,
          skipStepDelay: false,
        },
      );
    } catch (error) {
      if (isFatalPipelineError(error)) {
        throw error;
      }
      suffixOut = {
        ok: false,
        error: error?.message || String(error),
        steps: [],
      };
    }
    return { branchResults: prefixOuts, suffixOut };
  }

  const branchOuts = await settleBranchExecutions(plan.branches, runBranchSteps);
  return { branchResults: branchOuts, suffixOut: null };
}

async function resolveManualGoodsStampAssignment({
  nav,
  step,
  params,
  onGoodsStampAssignPrompt,
  rowIndex,
  row,
  defaultStamp,
  stampSourceLabel,
}) {
  if (nav === 'goods') return null;
  if (resolveStampAssignMode(params) !== STAMP_ASSIGN_MODE.RUNTIME_MANUAL) return null;
  if (typeof onGoodsStampAssignPrompt !== 'function') {
    throw new Error(i18n.t('workbench:ipeUtilError.stepRuntimeAssignUnsupported', { step: step.label || nav }));
  }
  const templateIds = Array.isArray(params.imageTemplateIds)
    ? params.imageTemplateIds.map(String).filter(Boolean)
    : [];
  if (templateIds.length === 0) {
    throw new Error(i18n.t('workbench:ipeUtilError.stepSelectTemplateSet', { step: step.label || nav }));
  }
  return onGoodsStampAssignPrompt({
    rowIndex,
    row,
    navId: nav,
    stepLabel: step.label || nav,
    templateIds,
    defaultStamp,
    stampSourceLabel,
  });
}

async function resolvePreAssignGoodsStepAssignment({
  nav,
  step,
  params,
  rowIndex,
  row,
  pipelineStamp,
  workflowContext = {},
}) {
  if (nav === 'goods') return null;
  if (resolveStampAssignMode(params) !== STAMP_ASSIGN_MODE.PRE_ASSIGN_BY_ROW) return null;

  const stampPreAssign = workflowContext.stampPreAssign;
  if (!stampPreAssign || stampPreAssign.navId !== nav) {
    throw new Error(i18n.t('workbench:ipeUtilError.stepMissingPreAssign', { step: step.label || nav }));
  }

  const taskId = workflowContext.workflowTaskId;
  if (!taskId) {
    throw new Error(i18n.t('workbench:ipeUtilError.stepPreAssignNoContext', { step: step.label || nav }));
  }

  if (!pipelineStamp?.spaceId || !pipelineStamp?.url) {
    throw new Error(i18n.t('workbench:ipeUtilError.stepMissingUpstreamStamp', { step: step.label || nav }));
  }

  registerPreAssignRowStampOutput(taskId, row.id, pipelineStamp);

  const templateIds = Array.isArray(params.imageTemplateIds)
    ? params.imageTemplateIds.map(String).filter(Boolean)
    : (stampPreAssign.templateIds || []).map(String).filter(Boolean);
  if (templateIds.length === 0) {
    throw new Error(i18n.t('workbench:ipeUtilError.stepSelectTemplateSet', { step: step.label || nav }));
  }

  const templates = await loadWorkflowStampTemplates(templateIds);
  const slotNames = resolveStampSlotNames(templates);
  const rowIds = (stampPreAssign.rowIds || []).map(String).filter(Boolean);
  const execMode = resolvePreAssignExecutionMode(rowIds, slotNames);
  const allRows = workflowContext.allRows || [];

  if (execMode === 'perRow') {
    const goodsForms = buildPerRowPreAssignGoodsForms({
      templates,
      slotNames,
      rowId: row.id,
      rowIds,
      pipelineStamp,
    });
    if (!goodsForms.length) {
      throw new Error(i18n.t('workbench:ipeUtilError.stepStampUnmapped', { step: step.label || nav }));
    }
    return { goodsForms, stampRefs: [] };
  }

  const neededRowIds = getNeededRowIdsForPreAssign(stampPreAssign, slotNames);
  const executorRowIndex = resolvePreAssignExecutorRowIndex(stampPreAssign, allRows, slotNames);
  const rowAssign = buildStampSlotAssignments(rowIds, slotNames)
    .find((item) => String(item.rowId) === String(row.id));
  const listingGroupIndex = Number.isFinite(Number(rowAssign?.groupIndex))
    ? Number(rowAssign.groupIndex)
    : 0;

  if (rowIndex !== executorRowIndex) {
    await waitForPreAssignRowStampOutputs(taskId, neededRowIds);
    const delegated = await waitForPreAssignGoodsComplete(taskId, executorRowIndex);
    if (!delegated?.spaceId || !delegated?.url) {
      throw new Error(i18n.t('workbench:ipeUtilError.stepMergeTimeout', { step: step.label || nav }));
    }
    return {
      __delegatedGoodsResult: delegated,
      __listingGroupIndex: listingGroupIndex,
      goodsForms: [],
      stampRefs: [],
    };
  }

  const rowStampOutputs = await waitForPreAssignRowStampOutputs(taskId, neededRowIds);
  const goodsForms = buildPreAssignGoodsForms({
    templates,
    rowIds,
    slotNames,
    rowStampOutputs,
  });
  if (!goodsForms.length) {
    throw new Error(i18n.t('workbench:ipeUtilError.stepPreAssignInvalid', { step: step.label || nav }));
  }
  return {
    goodsForms,
    stampRefs: [],
    __notifyGoodsComplete: true,
    __listingGroupIndex: listingGroupIndex,
    executorRowIndex,
  };
}

async function resolveGoodsStepAssignment(ctx) {
  const manual = await resolveManualGoodsStampAssignment(ctx);
  if (manual) return manual;
  return resolvePreAssignGoodsStepAssignment(ctx);
}

/**
 * 按工作流模板顺序执行（插件采集行）：图案裁剪 / 抠图 / 放大 / 印花提取 / 标题 / 主图创作 等。
 * 「主图创作」使用流水线当前图生成主图；「POD商品设计」使用设计模板套图合成。
 */
export async function runPipelineForRow(row, template, {
  onStep,
  onInfringementRiskPrompt,
  onGoodsStampAssignPrompt,
  rowIndex,
  workflowRemark = '',
  workflowRunId,
  workflowContext: workflowContextInput,
  shouldAbort,
  startStepIndex = 0,
  /** 阶段优先执行时用于「只跑完当前阶段就返回」：跑完该步骤（含）后立即返回 */
  endStepIndex = null,
  resumeFromResult = null,
  initialPipelineState = null,
  skipStepDelay = false,
} = {}) {
  const workflowContext = {
    ...(workflowContextInput || {}),
    row,
    workflowRunId: workflowContextInput?.workflowRunId ?? workflowRunId,
  };
  const allStages = resolveTemplateStepStages(template)
    .map((stage) => stage.filter((s) => s.navId !== 'sourcingHub'))
    .filter((stage) => stage.length > 0);
  const steps = allStages.flat();
  const out = {
    ok: true,
    steps: [],
    titleTaskId: null,
    finalSpaceId: null,
    finalImageUrl: null,
    error: null,
  };

  let currentSpaceId;
  let currentUrl;
  /** 仅由「印花图提取」步骤写入；供 POD 商品设计使用，主图创作走流水线当前图。 */
  let stampOutputSpaceId = null;
  let stampOutputUrl = null;

  const safeStartStep = initialPipelineState
    ? 0
    : alignResumeStepIndex(
      allStages,
      Math.max(0, Math.min(startStepIndex, steps.length)),
    );

  if (initialPipelineState) {
    currentSpaceId = initialPipelineState.currentSpaceId;
    currentUrl = initialPipelineState.currentUrl;
    stampOutputSpaceId = initialPipelineState.stampOutputSpaceId ?? null;
    stampOutputUrl = initialPipelineState.stampOutputUrl ?? null;
    out.initialImageUrl = initialPipelineState.initialImageUrl ?? currentUrl;
    out.finalSpaceId = currentSpaceId;
    out.finalImageUrl = currentUrl;
  } else if (safeStartStep > 0 && resumeFromResult) {
    out.steps = Array.isArray(resumeFromResult.steps)
      ? resumeFromResult.steps.slice(0, safeStartStep)
      : [];
    out.initialImageUrl = resumeFromResult.initialImageUrl;
    out.titleTaskId = resumeFromResult.titleTaskId || null;
    currentSpaceId = resumeFromResult.finalSpaceId;
    currentUrl = resumeFromResult.finalImageUrl;
    out.finalSpaceId = currentSpaceId;
    out.finalImageUrl = currentUrl;
    for (const stepRec of out.steps) {
      if (resolveWorkflowNavId(stepRec) === 'stampExtract') {
        stampOutputUrl = stepRec.outputImageUrl || stampOutputUrl;
        stampOutputSpaceId = stepRec.outputSpaceId || stampOutputSpaceId;
      }
    }
  } else {
    try {
      const init = await withWorkflowStepRetry(() => ensureInitialSpace(row));
      currentSpaceId = init.spaceId;
      currentUrl = init.imageUrl;
      out.finalSpaceId = currentSpaceId;
      out.finalImageUrl = currentUrl;
      out.initialImageUrl = currentUrl;
    } catch (e) {
      out.ok = false;
      out.error = e.message || String(e);
      return out;
    }
  }

  if (!initialPipelineState && safeStartStep === 0) {
    const plan = resolveTemplateExecutionPlan(template);
    if (plan.mode === 'parallel' && plan.branches.length > 1) {
      throwIfAborted(shouldAbort);
      const branchPack = await runBranchPipelinePlan(row, template, plan, {
        onStep,
        onInfringementRiskPrompt,
        onGoodsStampAssignPrompt,
        rowIndex,
        workflowRemark,
        workflowRunId,
        workflowContextInput,
        shouldAbort,
        inputSnap: buildPipelineInputSnapshot(out, currentSpaceId, currentUrl, stampOutputSpaceId, stampOutputUrl),
      });
      return mergeBranchPipelineOutput(out, template, branchPack.branchResults);
    }
    if (plan.mode === 'join' && plan.branches.length > 1 && plan.suffix.length > 0) {
      throwIfAborted(shouldAbort);
      const branchPack = await runBranchPipelinePlan(row, template, plan, {
        onStep,
        onInfringementRiskPrompt,
        onGoodsStampAssignPrompt,
        rowIndex,
        workflowRemark,
        workflowRunId,
        workflowContextInput,
        shouldAbort,
        inputSnap: buildPipelineInputSnapshot(out, currentSpaceId, currentUrl, stampOutputSpaceId, stampOutputUrl),
      });
      const merged = mergeBranchPipelineOutput(out, template, branchPack.branchResults);
      if (branchPack.suffixOut) {
        mergeBranchPipelineOutput(merged, template, [branchPack.suffixOut]);
      }
      return merged;
    }
  }

  let stageOffset = 0;
  for (let stageIndex = 0; stageIndex < allStages.length; stageIndex += 1) {
    const stage = allStages[stageIndex];
    const stageStart = stageOffset;
    stageOffset += stage.length;

    if (stageStart + stage.length <= safeStartStep) continue;

    if (stage.length > 1 && stageStart >= safeStartStep && !initialPipelineState) {
      throwIfAborted(shouldAbort);
      const inputSnap = {
        currentSpaceId,
        currentUrl,
        stampOutputSpaceId,
        stampOutputUrl,
        initialImageUrl: out.initialImageUrl,
      };

      const branchOuts = await Promise.all(
        stage.map((step, ji) => {
          const flatIndex = stageStart + ji;
          return runPipelineForRow(
            row,
            buildBranchExecutionTemplate(template, [step]),
            {
              onStep: (ev) => onStep?.({ ...ev, index: flatIndex }),
              onInfringementRiskPrompt,
              onGoodsStampAssignPrompt,
              rowIndex,
              workflowRemark,
              workflowRunId,
              workflowContext: workflowContextInput,
              shouldAbort,
              initialPipelineState: inputSnap,
              skipStepDelay: true,
            },
          );
        }),
      );

      let stageFailed = false;
      for (let ji = 0; ji < branchOuts.length; ji += 1) {
        const br = branchOuts[ji];
        const flatIndex = stageStart + ji;
        const step = stage[ji];
        const nav = resolveWorkflowNavId(step);
        if (br?.skipped) {
          out.ok = false;
          out.skipped = true;
          out.stoppedAtNavId = br.stoppedAtNavId || nav;
          out.error = br.error || '已终止当前条目';
          stageFailed = true;
        }
        if (br?.pendingReview) {
          out.pendingReview = true;
          out.reviewAtStepIndex = flatIndex;
          out.reviewStepLabel = step.label;
          out.reviewNavId = nav;
          out.ok = false;
          stageFailed = true;
        }
        if (br?.steps?.[0]) {
          out.steps.push(br.steps[0]);
        }
        if (br?.titleTaskId) out.titleTaskId = br.titleTaskId;
        if (br?.titleText) out.titleText = br.titleText;
        if (br?.copywritingTaskId) out.copywritingTaskId = br.copywritingTaskId;
        if (br?.copywritingText) out.copywritingText = br.copywritingText;
        if (br?.copywritingLanguage) out.copywritingLanguage = br.copywritingLanguage;
        if (br?.infringementBatchId) out.infringementBatchId = br.infringementBatchId;
        if (br?.infringementHighRisk) {
          out.infringementHighRisk = true;
          out.infringementRiskLevel = br.infringementRiskLevel;
          out.infringementNote = br.infringementNote;
        }
        if (br?.listingJobId) out.listingJobId = br.listingJobId;
        if (br?.sourcingItemIds) out.sourcingItemIds = br.sourcingItemIds;
        if (!br?.ok && !br?.skipped && !br?.pendingReview) {
          out.ok = false;
          out.error = br?.error || out.error;
          stageFailed = true;
        }
        if (br?.finalImageUrl) {
          currentUrl = br.finalImageUrl;
          currentSpaceId = br.finalSpaceId ?? currentSpaceId;
        }
        if (br?.steps) {
          for (const rec of br.steps) {
            if (resolveWorkflowNavId(rec) === 'stampExtract') {
              stampOutputUrl = rec.outputImageUrl || stampOutputUrl;
              stampOutputSpaceId = rec.stampOutputSpaceId || rec.outputSpaceId || stampOutputSpaceId;
            }
          }
        }
      }

      out.finalSpaceId = currentSpaceId;
      out.finalImageUrl = currentUrl;

      if (out.skipped) {
        return out;
      }

      if (out.pendingReview) return out;

      if (stageFailed) {
        return out;
      }

      continue;
    }

    const runFrom = Math.max(stageStart, safeStartStep);
    for (let i = runFrom; i < stageStart + stage.length; i += 1) {
    throwIfAborted(shouldAbort);
    const step = steps[i];
    const nav = resolveWorkflowNavId(step);
    if (!WORKFLOW_EXECUTABLE_NAV_IDS.has(nav)) {
      throw new Error(i18n.t('workbench:ipeUtilError.stepNotAutoExecutable', { step: step.label || nav || i + 1, navId: step.navId || '-' }));
    }
    if (out.steps.length !== i) {
      const prev = steps[i - 1];
      throw new Error(i18n.t('workbench:ipeUtilError.workflowOutOfOrder', { prev: prev?.label || prev?.navId || i, step: step.label || nav }));
    }
    const params = resolveStepParams(step, row);
    const stepInputSpaceId = currentSpaceId;
    const inputUrlBefore = currentUrl;
    const runningInputUrl = inputUrlBefore;
    onStep?.({
      index: i,
      navId: nav,
      label: step.label,
      status: 'running',
      inputImageUrl: runningInputUrl,
      outputImageUrl: '',
    });

    let stepRecorded = false;
    try {
      if (nav === 'imageCrop') {
        const ref = await withWorkflowStepRetry(() => runCropStep(row, stepInputSpaceId, inputUrlBefore, workflowContext, shouldAbort));
        currentSpaceId = ref.spaceId;
        currentUrl = ref.imageUrl;
        out.steps.push({
          navId: nav,
          label: step.label,
          status: 'ok',
          taskId: ref.taskId,
          previewLeftLabel: '本步输入',
          previewRightLabel: '裁剪产出',
          inputImageUrl: tracePreviewUrl(inputUrlBefore, 'crop-in', stepInputSpaceId),
          outputImageUrl: tracePreviewUrl(currentUrl, 'crop-out', ref.spaceId),
        });
        stepRecorded = true;
      } else if (nav === 'smartCutout') {
        const ref = await withWorkflowStepRetry(() => runCutoutStep(row, stepInputSpaceId, inputUrlBefore, params, workflowContext, shouldAbort));
        currentSpaceId = ref.spaceId;
        currentUrl = ref.imageUrl;
        out.steps.push({
          navId: nav,
          label: step.label,
          status: 'ok',
          taskId: ref.taskId,
          previewLeftLabel: '本步输入',
          previewRightLabel: '抠图产出',
          inputImageUrl: tracePreviewUrl(inputUrlBefore, 'cut-in', stepInputSpaceId),
          outputImageUrl: tracePreviewUrl(currentUrl, 'cut-out', ref.spaceId),
        });
        stepRecorded = true;
      } else if (nav === 'imageEnhance') {
        const ref = await withWorkflowStepRetry(() => runEnhanceStep(row, stepInputSpaceId, inputUrlBefore, params, workflowContext, shouldAbort));
        currentSpaceId = ref.spaceId;
        currentUrl = ref.imageUrl;
        out.steps.push({
          navId: nav,
          label: step.label,
          status: 'ok',
          taskId: ref.taskId,
          previewLeftLabel: '本步输入',
          previewRightLabel: '放大产出',
          inputImageUrl: tracePreviewUrl(inputUrlBefore, 'enh-in', stepInputSpaceId),
          outputImageUrl: tracePreviewUrl(currentUrl, 'enh-out', ref.spaceId),
        });
        stepRecorded = true;
      } else if (nav === 'stampExtract') {
        const refStamp = await withWorkflowStepRetry(() => runStampExtractStep(row, stepInputSpaceId, inputUrlBefore, workflowContext, shouldAbort));
        stampOutputSpaceId = refStamp.spaceId;
        stampOutputUrl = refStamp.imageUrl;
        out.steps.push({
          navId: nav,
          label: step.label,
          status: 'ok',
          taskId: refStamp.taskId,
          note: '印花结果已写入图片空间；后续「POD商品设计」可用该图作为印花源',
          previewLeftLabel: '本步输入（流水线当前图）',
          previewRightLabel: '印花结果',
          inputImageUrl: tracePreviewUrl(inputUrlBefore, 'stamp-in', stepInputSpaceId),
          outputImageUrl: tracePreviewUrl(stampOutputUrl, 'stamp-out', refStamp.spaceId),
          stampOutputSpaceId: refStamp.spaceId,
        });
        stepRecorded = true;
      } else if (nav === 'goods') {
        if (!stepInputSpaceId || !inputUrlBefore) {
          throw new Error(i18n.t('workbench:ipeUtilError.goodsMissingUpstream'));
        }
        const ref = await withWorkflowStepRetry(() =>
          runGoodsAiStudioWorkflowStep({
            row,
            printSpaceId: stepInputSpaceId,
            printImageUrl: inputUrlBefore,
            params,
            workflowContext,
            billingNav: 'goods',
          }),
        );
        currentSpaceId = ref.spaceId;
        currentUrl = ref.imageUrl;
        out.steps.push(withStepOutputDisplay({
          navId: nav,
          label: step.label,
          status: 'ok',
          goodsTaskId: ref.taskId,
          outputSpaceId: ref.spaceId,
          compositeImageUrls: ref.compositeImageUrls || [],
          mainImageCount: ref.mainImageCount,
          detailPageCount: ref.detailPageCount,
          previewLeftLabel: '本步输入',
          previewRightLabel: '主图创作',
          inputImageUrl: tracePreviewUrl(inputUrlBefore, 'goods-in', stepInputSpaceId),
          outputImageUrl: tracePreviewUrl(currentUrl, 'goods-out', ref.spaceId),
        }, ref));
        stepRecorded = true;
      } else if (nav === 'titles' || nav === 'titleExtract') {
        const ref = await withWorkflowStepRetry(() =>
          runLegacyTitlesAsAiCopywriting(row, params, stepInputSpaceId, inputUrlBefore, workflowContext),
        );
        out.titleTaskId = ref.taskId;
        out.titleText = ref.titleText;
        out.copywritingTaskId = ref.taskId;
        out.copywritingText = ref.copywritingText;
        out.copywritingLanguage = ref.language;
        out.steps.push({
          navId: nav,
          label: step.label || 'AI文案',
          status: 'ok',
          titleTaskId: ref.taskId,
          titleText: ref.titleText,
          language: ref.language,
          note: ref.note || (ref.titleText ? `文案：${ref.titleText}` : 'AI文案已完成'),
          previewLeftLabel: '参考主图（流水线当前图）',
          previewRightLabel: '同左（本步产出为文案文本，见下方说明）',
          inputImageUrl: inputUrlBefore,
          outputImageUrl: currentUrl,
        });
        stepRecorded = true;
      } else if (nav === 'infringementDetection') {
        const ref = await withWorkflowStepRetry(() =>
          runInfringementStep(row, inputUrlBefore, params, {
            onInfringementRiskPrompt: onInfringementRiskPrompt
              ? (ctx) => onInfringementRiskPrompt({ ...ctx, rowIndex })
              : undefined,
          }),
        );
        out.infringementBatchId = ref.batchId;
        out.steps.push({
          navId: nav,
          label: step.label,
          status: 'ok',
          infringementBatchId: ref.batchId,
          infringementBatchNo: ref.batchNo,
          riskLevel: ref.riskLevel,
          highRisk: Boolean(ref.highRisk),
          note: ref.note,
          previewLeftLabel: '检测原图',
          previewRightLabel: `检测结果（${ref.riskLevel || '—'}）`,
          inputImageUrl: tracePreviewUrl(inputUrlBefore, 'infr-in', stepInputSpaceId),
          outputImageUrl: tracePreviewUrl(inputUrlBefore, 'infr-out', stepInputSpaceId),
        });
        if (ref.highRisk) {
          out.infringementHighRisk = true;
          out.infringementRiskLevel = ref.riskLevel;
          out.infringementNote = ref.note;
        }
        stepRecorded = true;
      } else if (isAiImageWorkflowNav(nav)) {
        const ref = await withWorkflowStepRetry(() =>
          runAiImageWorkflowStep({
            navId: nav,
            row,
            currentSpaceId: stepInputSpaceId,
            currentUrl: inputUrlBefore,
            params,
            workflowContext,
          }),
        );
        currentSpaceId = ref.spaceId;
        currentUrl = ref.imageUrl;
        out.steps.push(withStepOutputDisplay({
          navId: nav,
          label: step.label,
          status: 'ok',
          taskId: ref.taskId,
          compositeImageUrls: ref.compositeImageUrls || [],
          previewLeftLabel: '本步输入',
          previewRightLabel: '本步产出',
          inputImageUrl: tracePreviewUrl(inputUrlBefore, `${nav}-in`, stepInputSpaceId),
          outputImageUrl: tracePreviewUrl(ref.imageUrl || currentUrl, `${nav}-out`, ref.spaceId || currentSpaceId),
        }, ref));
        stepRecorded = true;
      } else if (nav === 'podProductDesign') {
        const assignmentResult = await resolveGoodsStepAssignment({
          nav,
          step,
          params,
          onGoodsStampAssignPrompt,
          rowIndex,
          row,
          defaultStamp: {
            spaceId: stepInputSpaceId,
            url: inputUrlBefore,
            label: '流水线当前图',
          },
          stampSourceLabel: '本步输入（作为默认印花候选）',
          pipelineStamp: {
            spaceId: stepInputSpaceId,
            url: inputUrlBefore,
            name: String(row?.title || row?.name || '').trim(),
          },
          workflowContext,
        });
        let ref;
        if (assignmentResult?.__delegatedGoodsResult) {
          const delegated = assignmentResult.__delegatedGoodsResult;
          // 多印花组预分配：按本行所属 listing 组取主图，避免全员共用第 0 组黑/白主图
          const assignGroupIndex = Number(
            assignmentResult.__listingGroupIndex
            ?? delegated.listingGroupIndex
            ?? 0,
          );
          const groupMains = Array.isArray(delegated.mainsByListingGroup)
            ? delegated.mainsByListingGroup[assignGroupIndex]
            : null;
          const mainEntries = Array.isArray(groupMains) && groupMains.length > 0
            ? groupMains
            : (Array.isArray(delegated.mainCompositeMetas) ? delegated.mainCompositeMetas : []);
          const mainUrls = mainEntries.length > 0
            ? mainEntries.map((row) => String(row?.url || '').trim()).filter(Boolean)
            : (Array.isArray(delegated.mainCompositeImageUrls) ? delegated.mainCompositeImageUrls : []);
          ref = {
            taskId: delegated.taskId,
            ...attachWorkflowImagePreview(delegated, delegated.compositeImageUrls),
            listingGroupIndex: Number.isFinite(assignGroupIndex) ? assignGroupIndex : 0,
            mainCompositeImageUrls: mainUrls,
            mainCompositeMetas: mainEntries,
            mainsByListingGroup: delegated.mainsByListingGroup,
          };
        } else {
          ref = await withWorkflowStepRetry(() =>
            runGoodsWorkflowStep({
              row,
              printSpaceId: stepInputSpaceId,
              printImageUrl: inputUrlBefore,
              params,
              workflowContext,
              assignment: assignmentResult?.goodsForms?.length
                ? {
                  goodsForms: assignmentResult.goodsForms,
                  stampRefs: assignmentResult.stampRefs || [],
                }
                : null,
              billingNav: 'podProductDesign',
            }),
          );
          if (assignmentResult?.__notifyGoodsComplete) {
            notifyPreAssignGoodsComplete(
              workflowContext.workflowTaskId,
              assignmentResult.executorRowIndex ?? rowIndex,
              ref,
            );
          }
          // 执行行也按预分配组号裁剪主图列表，避免后续主图创作串色
          if (Number.isFinite(Number(assignmentResult?.__listingGroupIndex))) {
            const gi = Number(assignmentResult.__listingGroupIndex);
            const groupMains = Array.isArray(ref.mainsByListingGroup)
              ? ref.mainsByListingGroup[gi]
              : null;
            if (Array.isArray(groupMains) && groupMains.length > 0) {
              ref = {
                ...ref,
                listingGroupIndex: gi,
                mainCompositeMetas: groupMains,
                mainCompositeImageUrls: groupMains
                  .map((entry) => String(entry?.url || '').trim())
                  .filter(Boolean),
              };
            } else {
              ref = { ...ref, listingGroupIndex: gi };
            }
          }
        }
        currentSpaceId = ref.spaceId;
        currentUrl = ref.imageUrl;
        out.steps.push(withStepOutputDisplay({
          navId: nav,
          label: step.label,
          status: 'ok',
          goodsTaskId: ref.taskId,
          outputSpaceId: ref.spaceId,
          compositeImageUrls: ref.compositeImageUrls || [],
          mainCompositeImageUrls: Array.isArray(ref.mainCompositeImageUrls)
            ? ref.mainCompositeImageUrls
            : [],
          mainCompositeMetas: Array.isArray(ref.mainCompositeMetas) ? ref.mainCompositeMetas : [],
          listingGroupIndex: Number.isFinite(Number(ref.listingGroupIndex))
            ? Number(ref.listingGroupIndex)
            : 0,
          previewLeftLabel: '本步输入（流水线当前图作为印花）',
          previewRightLabel: 'POD套图合成产出',
          inputImageUrl: tracePreviewUrl(inputUrlBefore, 'pod-in', stepInputSpaceId),
          outputImageUrl: tracePreviewUrl(currentUrl, 'pod-out', ref.spaceId),
        }, ref));
        stepRecorded = true;
      } else if (nav === 'videos' || nav === 'textToVideo') {
        const ref = await withWorkflowStepRetry(() =>
          runVideoWorkflowStep({
            row,
            currentSpaceId: stepInputSpaceId,
            currentUrl: inputUrlBefore,
            params,
            workflowContext,
            navId: nav,
          }),
        );
        // 视频为旁路产出，流水线当前图保持不变供后续节点使用
        out.videoTaskId = ref.taskId;
        out.videoTaskIds = ref.taskIds || [];
        out.outputVideoUrl = ref.outputVideoUrl;
        out.outputVideoUrls = ref.videoUrls || [];
        const isT2V = nav === 'textToVideo';
        out.steps.push({
          navId: nav,
          label: step.label,
          status: 'ok',
          videoTaskId: ref.taskId,
          videoTaskIds: ref.taskIds || [],
          outputVideoUrl: ref.outputVideoUrl,
          outputVideoUrls: ref.videoUrls || [],
          note: ref.videoUrls?.length > 1
            ? `已生成 ${ref.videoUrls.length} 条视频（旁路产出，当前图不变）`
            : (isT2V ? '文生视频已完成（旁路产出，当前图不变）' : '图生视频已完成（旁路产出，当前图不变）'),
          previewLeftLabel: isT2V ? '视频描述' : '首帧（流水线当前图，本步不改）',
          previewRightLabel: '视频产出（旁路）',
          inputImageUrl: isT2V ? '' : tracePreviewUrl(inputUrlBefore, 'video-in', stepInputSpaceId),
          outputImageUrl: '',
        });
        stepRecorded = true;
      } else if (isModelStudioNav(nav)) {
        const ref = await withWorkflowStepRetry(() =>
          runModelStudioWorkflowStep({
            navId: nav,
            row,
            currentSpaceId: stepInputSpaceId,
            currentUrl: inputUrlBefore,
            params,
            workflowContext,
          }),
        );
        currentSpaceId = ref.spaceId;
        currentUrl = ref.imageUrl;
        const modelStudioStepExtras = (() => {
          if (nav === 'modelTryOn' && ref.result) {
            const modelUrl = resolveModelTryOnModelPreviewUrl(ref.result);
            if (!modelUrl) return {};
            const reuseModel = resolveTryOnReuseModelState(ref.result);
            return {
              modelReferenceImageUrl: modelUrl,
              modelReferencePresetId: ref.result?.selectedPresetId || '',
              modelReferenceModelType: ref.result?.modelType || '',
              modelReferenceGarmentType: ref.result?.garmentType || '',
              modelReferenceUsedUploadedModel: reuseModel.usedUploadedModel,
              previewLeftLabel: '本步输入（服饰图）',
              previewCenterLabel: reuseModel.usedUploadedModel ? '模特图' : '系统模特',
              previewRightLabel: '试衣生成图',
            };
          }
          if ((nav === 'modelHandheld' || nav === 'modelUniversalWear') && ref.result) {
            const modelUrl = resolveModelEthnicityModelPreviewUrl(ref.result);
            const leftLabel = nav === 'modelHandheld' ? '本步输入（商品图）' : '本步输入（穿戴物图）';
            const rightLabel = nav === 'modelHandheld' ? '手持商品生成图' : '万物穿戴生成图';
            if (!modelUrl) {
              return { previewLeftLabel: leftLabel, previewRightLabel: rightLabel };
            }
            const reuseModel = resolveModelEthnicityReuseModelState(ref.result);
            return {
              modelReferenceImageUrl: modelUrl,
              modelReferencePresetId: ref.result?.selectedPresetId || '',
              modelReferenceModelType: ref.result?.modelType || '',
              modelReferenceGarmentType: ref.result?.modelEthnicity || '',
              modelReferenceUsedUploadedModel: reuseModel.usedUploadedModel,
              previewLeftLabel: leftLabel,
              previewCenterLabel: reuseModel.usedUploadedModel ? '模特图' : '系统模特',
              previewRightLabel: rightLabel,
            };
          }
          if (nav === 'modelHandheld') {
            return { previewLeftLabel: '本步输入（商品图）', previewRightLabel: '手持商品生成图' };
          }
          if (nav === 'modelUniversalWear') {
            return { previewLeftLabel: '本步输入（穿戴物图）', previewRightLabel: '万物穿戴生成图' };
          }
          return {
            previewLeftLabel: '本步输入（服饰/商品图）',
            previewRightLabel: '试衣/模特生成图',
          };
        })();
        out.steps.push({
          navId: nav,
          label: step.label,
          status: 'ok',
          taskId: ref.taskId,
          compositeImageUrls: ref.compositeImageUrls || [],
          inputImageUrl: tracePreviewUrl(inputUrlBefore, `${nav}-in`, stepInputSpaceId),
          outputImageUrl: tracePreviewUrl(currentUrl, `${nav}-out`, ref.spaceId),
          ...modelStudioStepExtras,
        });
        stepRecorded = true;
      } else if (nav === 'aiCopywriting') {
        const ref = await withWorkflowStepRetry(() =>
          runAiCopywritingWorkflowStep(row, stepInputSpaceId, inputUrlBefore, params, workflowContext),
        );
        out.copywritingTaskId = ref.taskId;
        out.copywritingText = ref.copywritingText;
        out.copywritingLanguage = ref.language;
        out.steps.push({
          navId: nav,
          label: step.label,
          status: 'ok',
          copywritingTaskId: ref.taskId,
          copywritingText: ref.copywritingText,
          language: ref.language,
          note: ref.note || 'AI 文案已生成',
          previewLeftLabel: '参考图（流水线当前图）',
          previewRightLabel: '同左（本步产出为文案，见下方说明）',
          inputImageUrl: tracePreviewUrl(inputUrlBefore, 'copy-in', stepInputSpaceId),
          outputImageUrl: tracePreviewUrl(currentUrl, 'copy-out', currentSpaceId),
        });
        stepRecorded = true;
      } else if (nav === 'platformListing') {
        const exportViaTemplate =
          Array.isArray(params.listingTemplateIds) &&
          params.listingTemplateIds.length > 0 &&
          params.autoCreateListingJob !== true;
        if (exportViaTemplate) {
          out.steps.push({
            navId: nav,
            label: step.label,
            status: 'ok',
            note: `条目 ${row?.id ?? ''} 已完成，任务结束后统一导出表格与结果图`,
            previewLeftLabel: '待上架商品图',
            previewRightLabel: '待统一导出',
            inputImageUrl: tracePreviewUrl(inputUrlBefore, 'list-in', stepInputSpaceId),
            outputImageUrl: tracePreviewUrl(currentUrl, 'list-out', currentSpaceId),
          });
          stepRecorded = true;
        } else {
          const ref = await withWorkflowStepRetry(() =>
            runPlatformListingStep(row, inputUrlBefore, params, workflowRemark, out),
          );
          out.listingJobId = ref.listingJobId;
          out.scheduledListingId = ref.scheduledListingId;
          out.sourcingItemIds = ref.sourcingItemIds;
          out.steps.push({
            navId: nav,
            label: step.label,
            status: 'ok',
            listingJobId: ref.listingJobId,
            scheduledListingId: ref.scheduledListingId,
            sourcingBatchKey: ref.batchKey,
            batchKey: ref.batchKey,
            sourcingItemIds: ref.sourcingItemIds || [],
            listingAutoRuleNames: ref.listingAutoRuleNames || [],
            note: ref.note,
            previewLeftLabel: '待上架商品图',
            previewRightLabel: ref.listingJobId
              ? '刊登任务已创建'
              : (ref.scheduledListingId ? '已加入定时上架' : '已入库待上品'),
            inputImageUrl: tracePreviewUrl(inputUrlBefore, 'list-in', stepInputSpaceId),
            outputImageUrl: tracePreviewUrl(currentUrl, 'list-out', currentSpaceId),
          });
        }
        stepRecorded = true;
      }

      if (!stepRecorded) {
        throw new Error(i18n.t('workbench:ipeUtilError.stepLogicMissing', { step: step.label || nav, nav }));
      }

      // 步骤间延迟，避免单个条目内部过快触发限流（并行分支内跳过）
      if (!skipStepDelay && i < steps.length - 1) {
        throwIfAborted(shouldAbort);
        await sleepWithJitter(STEP_DELAY_MS);
      }

      out.finalSpaceId = currentSpaceId;
      out.finalImageUrl = currentUrl;
      const lastStep = out.steps[out.steps.length - 1];
      if (!lastStep || resolveWorkflowNavId(lastStep) !== nav) {
        throw new Error(i18n.t('workbench:ipeUtilError.stepNoValidResult', { step: step.label || nav }));
      }
      let uiStatus = 'done';
      if (lastStep?.status === 'skipped') uiStatus = 'skipped';
      else if (lastStep?.highRisk) uiStatus = 'risk';
      onStep?.({
        index: i,
        navId: nav,
        label: step.label,
        status: uiStatus,
        detail: lastStep?.note || '',
        inputImageUrl: lastStep.inputImageUrl,
        outputImageUrl: lastStep.outputImageUrl,
        compositeImageUrls: lastStep.compositeImageUrls || [],
        previewLeftLabel: lastStep.previewLeftLabel,
        previewCenterLabel: lastStep.previewCenterLabel || '',
        previewRightLabel: lastStep.previewRightLabel,
        modelReferenceImageUrl: lastStep.modelReferenceImageUrl || '',
        modelReferencePresetId: lastStep.modelReferencePresetId || '',
        modelReferenceModelType: lastStep.modelReferenceModelType || '',
        modelReferenceGarmentType: lastStep.modelReferenceGarmentType || '',
        modelReferenceUsedUploadedModel: lastStep.modelReferenceUsedUploadedModel === true,
        outputVideoUrl: lastStep.outputVideoUrl || '',
        outputVideoUrls: Array.isArray(lastStep.outputVideoUrls) ? lastStep.outputVideoUrls : [],
        taskId: lastStep.taskId || '',
        goodsTaskId: lastStep.goodsTaskId || '',
        videoTaskId: lastStep.videoTaskId || '',
        copywritingTaskId: lastStep.copywritingTaskId || lastStep.titleTaskId || '',
        listingJobId: lastStep.listingJobId || '',
        listingJobIds: Array.isArray(lastStep.listingJobIds) ? lastStep.listingJobIds : [],
        infringementBatchId: lastStep.infringementBatchId || '',
        batchId: lastStep.batchId || lastStep.infringementBatchId || '',
        sourcingBatchKey: lastStep.sourcingBatchKey || lastStep.batchKey || '',
      });

    } catch (e) {
      if (e?.name === 'WorkflowTaskAbortedError' || e?.message === '任务已终止') {
        throw e;
      }
      if (e?.skipRow || e?.name === 'WorkflowRowSkippedError') {
        const msg = e.message || '已终止当前条目';
        out.ok = false;
        out.skipped = true;
        out.stoppedAtNavId = nav;
        out.error = msg;
        const [skipL, skipR] =
          nav === 'infringementDetection'
            ? ['检测原图', '侵权检测结果']
            : ['步骤前', '本步产出'];
        out.steps.push({
          navId: nav,
          label: step.label,
          status: 'skipped',
          note: msg,
          previewLeftLabel: skipL,
          previewRightLabel: skipR,
          inputImageUrl: tracePreviewUrl(inputUrlBefore, `${nav}-skip-in`, stepInputSpaceId),
          outputImageUrl: tracePreviewUrl(currentUrl, `${nav}-skip-out`, currentSpaceId),
        });
        onStep?.({
          index: i,
          navId: nav,
          label: step.label,
          status: nav === 'infringementDetection' ? 'stopped' : 'skipped',
          detail: msg,
          inputImageUrl: tracePreviewUrl(inputUrlBefore, `${nav}-skip-in`, stepInputSpaceId),
          outputImageUrl: tracePreviewUrl(currentUrl, `${nav}-skip-out`, currentSpaceId),
          previewLeftLabel: skipL,
          previewRightLabel: skipR,
        });
        break;
      }
      const msg = e.message || String(e);
      out.ok = false;
      out.error = msg;
      const [failL, failR] = (() => {
        switch (nav) {
          case 'imageCrop':
            return ['本步输入', '裁剪产出'];
          case 'smartCutout':
            return ['本步输入', '抠图产出'];
          case 'imageEnhance':
            return ['本步输入', '放大产出'];
          case 'stampExtract':
            return ['本步输入（流水线当前图）', '印花结果'];
          case 'goods':
            return ['本步输入', '主图创作'];
          case 'titles':
            return ['参考主图', '标题文本'];
          case 'infringementDetection':
            return ['检测原图', '侵权检测结果'];
          case 'platformListing':
            return ['待上架商品图', '上架结果'];
          case 'modelTryOn':
            return ['本步输入（服饰图）', '试衣生成图'];
          case 'modelHandheld':
            return ['本步输入（商品图）', '手持商品生成图'];
          case 'modelUniversalWear':
            return ['本步输入（穿戴物图）', '万物穿戴生成图'];
          case 'modelPoseFission':
            return ['本步输入（模特图）', '姿势裂变生成图'];
          case 'aiCopywriting':
            return ['参考主图', 'AI 文案'];
          case 'img2img':
            return ['本步输入', '改图产出'];
          case 'imageFission':
            return ['本步输入', '裂变产出'];
          case 'aiOutpaint':
            return ['本步输入', '扩图产出'];
          case 'aiTextToImage':
            return ['提示词参考', '文生图产出'];
          case 'podProductDesign':
            return ['本步输入（印花源）', 'POD套图产出'];
          case 'videos':
            return ['首帧（流水线当前图，本步不改）', '视频产出（旁路）'];
          case 'textToVideo':
            return ['视频描述', '视频产出（旁路）'];
          default:
            return ['步骤前', '本步产出'];
        }
      })();
      out.steps.push({
        navId: nav,
        label: step.label,
        status: 'failed',
        error: msg,
        previewLeftLabel: failL,
        previewRightLabel: failR,
        inputImageUrl: tracePreviewUrl(inputUrlBefore, `${nav}-fail-in`, stepInputSpaceId),
        outputImageUrl: '',
      });
      onStep?.({
        index: i,
        navId: nav,
        label: step.label,
        status: 'failed',
        error: msg,
        inputImageUrl: tracePreviewUrl(inputUrlBefore, `${nav}-fail-in`, stepInputSpaceId),
        outputImageUrl: '',
        previewLeftLabel: failL,
        previewRightLabel: failR,
      });

      // 失败后处理策略（模板中每个节点独立配置，默认「终止整批」）
      // 未执行的后续模块保持待执行，不写成跳过/终止。
      const failurePolicy = normalizeStepFailurePolicy(step.onFailure);
      if (failurePolicy === STEP_FAILURE_POLICY_SKIP) {
        break;
      }
      throw createBatchTerminateError(step, nav, msg, {
        ...out,
        ok: false,
        error: msg,
        batchTerminated: true,
      });
    }
    }

    // 阶段优先模式：当前阶段（单步骤）跑完后立即交还控制权，由上层推进到下一个模块
    if (endStepIndex != null && stageStart + stage.length - 1 >= endStepIndex) {
      out.finalSpaceId = currentSpaceId;
      out.finalImageUrl = currentUrl;
      return out;
    }
  }

  return out;
}

/**
 * 模块（阶段）优先批量执行：先把所有条目在第 1 个模块处理完，再进入下一个模块。
 * 每个模块对全部条目分批并发执行同一阶段，阶段产出沿条目向下传递。
 */
async function runPipelineStageFirst(effectiveRows, template, {
  onRowStart,
  onStep,
  onRowDone,
  onRowProgress,
  onInfringementRiskPrompt,
  onGoodsStampAssignPrompt,
  workflowRemark,
  workflowRunId,
  baseWorkflowContext,
  shouldAbort,
  resolveRowIndex,
  total,
  stageCount,
  startStage,
  rowResumeOptions,
}) {
  const localIndices = effectiveRows.map((_, i) => i);
  const prevStageResult = new Array(effectiveRows.length).fill(null);
  const finalEntries = new Array(effectiveRows.length).fill(null);
  const recorded = new Array(effectiveRows.length).fill(false);
  const stopped = new Array(effectiveRows.length).fill(false);

  const abortIfNeeded = () => {
    if (typeof shouldAbort === 'function' && shouldAbort()) {
      const err = new Error('任务已终止');
      err.name = 'WorkflowTaskAbortedError';
      throw err;
    }
  };

  const recordRowDone = (i, result) => {
    if (recorded[i]) return;
    recorded[i] = true;
    stopped[i] = true;
    finalEntries[i] = { row: effectiveRows[i], result };
    onRowDone?.({ rowIndex: resolveRowIndex(i), row: effectiveRows[i], total, result });
  };

  const emitRowProgress = (i, result, inProgress) => {
    if (recorded[i] || !result) return;
    onRowProgress?.({
      rowIndex: resolveRowIndex(i),
      row: effectiveRows[i],
      total,
      result: { ...result, inProgress: Boolean(inProgress) },
    });
  };

  /* ---------------- 「一个模块一个批次」批量阶段执行 ---------------- */

  // 展平为「一步一阶段」，禁止把文案等节点并进上一模块并行（那会退回逐组建任务）
  const stageDefs = resolveTemplateStepStages(template)
    .map((stage) => stage.filter((s) => s.navId !== 'sourcingHub'))
    .filter((stage) => stage.length > 0)
    .flat()
    .map((step) => [step]);
  const allStepsFlat = stageDefs.flat();
  // 这些步骤的产出不推进「流水线当前图」，后续节点仍用上一步的图
  const NON_ADVANCING_NAV_IDS = new Set([
    'stampExtract',
    'aiCopywriting',
    'titles',
    'titleExtract',
    'infringementDetection',
    'platformListing',
  ]);
  // 后端一次任务即可承载多条 source 的模块
  const STAGE_BATCH_NAV_IDS = new Set([
    'imageCrop',
    'smartCutout',
    'imageEnhance',
    'stampExtract',
    'img2img',
    'aiOutpaint',
    'aiTextToImage',
    'imageFission',
    'modelTryOn',
    'modelHandheld',
    'modelPoseFission',
    'modelUniversalWear',
    'goods',
    'podProductDesign',
    'aiCopywriting',
    'titles',
    'titleExtract',
    'infringementDetection',
    'platformListing',
  ]);

  const resolveRowBase = (i, input) => {
    const prev = prevStageResult[i];
    if (prev) return prev;
    const resume = rowResumeOptions[i]?.resumeFromResult || null;
    const startIdx = alignResumeStepIndex(
      allStepsFlat,
      Number(rowResumeOptions[i]?.startStepIndex) || 0,
    );
    return {
      ok: true,
      steps: resume && Array.isArray(resume.steps) ? resume.steps.slice(0, startIdx) : [],
      titleTaskId: resume?.titleTaskId || null,
      titleText: resume?.titleText,
      copywritingTaskId: resume?.copywritingTaskId,
      copywritingText: resume?.copywritingText,
      copywritingLanguage: resume?.copywritingLanguage,
      infringementBatchId: resume?.infringementBatchId,
      listingJobId: resume?.listingJobId,
      initialImageUrl: resume?.initialImageUrl || input?.url,
      finalSpaceId: input?.spaceId ?? resume?.finalSpaceId,
      finalImageUrl: input?.url ?? resume?.finalImageUrl,
      error: null,
    };
  };

  /** 单条目失败/跳过：只记当前模块，未跑到的后续模块保持待执行。 */
  const settleRowFailure = (i, msg, stageIndex, step, nav, forceSkip, options = {}) => {
    const throwOnTerminate = options.throwOnTerminate !== false;
    const input = {
      spaceId: prevStageResult[i]?.finalSpaceId,
      url: prevStageResult[i]?.finalImageUrl,
    };
    const base = resolveRowBase(i, input);
    const policy = normalizeStepFailurePolicy(step.onFailure);
    // 已开启上架却校验/刊登失败：记失败，不记跳过（跳过仅用于未开上架或用户显式跳过）
    const isSkip = Boolean(forceSkip) || policy === STEP_FAILURE_POLICY_SKIP;
    const steps = [...(base.steps || [])];
    const rec = allStepsFlat[stageIndex];
    if (rec) {
      steps.push({
        navId: resolveWorkflowNavId(rec),
        label: rec.label,
        status: isSkip ? 'skipped' : 'failed',
        error: isSkip ? undefined : msg,
        note: options.note || msg,
        ...(options.batchKey ? {
          batchKey: options.batchKey,
          sourcingBatchKey: options.batchKey,
        } : {}),
        ...(options.listingJobId != null ? { listingJobId: options.listingJobId } : {}),
        ...(Array.isArray(options.listingAutoRuleNames) && options.listingAutoRuleNames.length
          ? { listingAutoRuleNames: options.listingAutoRuleNames }
          : {}),
        ...(Array.isArray(options.sourcingItemIds) && options.sourcingItemIds.length
          ? { sourcingItemIds: options.sourcingItemIds }
          : {}),
      });
    }
    const result = {
      ...base,
      ok: false,
      error: msg,
      skipped: isSkip,
      stoppedAtNavId: isSkip ? undefined : nav,
      ...(options.batchKey ? { sourcingBatchKey: options.batchKey, batchKey: options.batchKey } : {}),
      ...(options.listingJobId != null ? { listingJobId: options.listingJobId } : {}),
      steps,
    };
    prevStageResult[i] = result;
    onStep?.({
      rowIndex: resolveRowIndex(i),
      row: effectiveRows[i],
      total,
      stageIndex,
      stageTotal: stageCount,
      index: stageIndex,
      navId: nav,
      label: step.label,
      status: isSkip ? 'skipped' : 'failed',
      detail: msg,
      error: msg,
    });
    recordRowDone(i, result);
    if (!isSkip) {
      const err = createBatchTerminateError(step, nav, msg, result);
      err.stageFirstRowIndex = resolveRowIndex(i);
      if (throwOnTerminate) throw err;
      return err;
    }
    return null;
  };

  const buildBatchStepRecord = (nav, step, entry, input) => {
    const inputImageUrl = tracePreviewUrl(input.url, `${nav}-in`, input.spaceId);
    const outputImageUrl = entry.imageUrl
      ? tracePreviewUrl(entry.imageUrl, `${nav}-out`, entry.spaceId)
      : '';
    const base = { navId: nav, label: step.label, status: 'ok', taskId: entry.taskId };
    if (nav === 'imageCrop') {
      return { ...base, previewLeftLabel: '本步输入', previewRightLabel: '裁剪产出', inputImageUrl, outputImageUrl };
    }
    if (nav === 'smartCutout') {
      return { ...base, previewLeftLabel: '本步输入', previewRightLabel: '抠图产出', inputImageUrl, outputImageUrl };
    }
    if (nav === 'imageEnhance') {
      return { ...base, previewLeftLabel: '本步输入', previewRightLabel: '放大产出', inputImageUrl, outputImageUrl };
    }
    if (nav === 'stampExtract') {
      return {
        ...base,
        note: '印花结果已写入图片空间；后续「POD商品设计」可用该图作为印花源',
        previewLeftLabel: '本步输入（流水线当前图）',
        previewRightLabel: '印花结果',
        inputImageUrl,
        outputImageUrl,
        stampOutputSpaceId: entry.spaceId,
      };
    }
    if (nav === 'goods' || nav === 'podProductDesign') {
      return {
        ...base,
        goodsTaskId: entry.taskId,
        outputSpaceId: entry.spaceId,
        compositeImageUrls: entry.compositeImageUrls || [],
        mainCompositeImageUrls: Array.isArray(entry.mainCompositeImageUrls)
          ? entry.mainCompositeImageUrls
          : [],
        mainCompositeMetas: Array.isArray(entry.mainCompositeMetas) ? entry.mainCompositeMetas : [],
        podMainOverlays: Array.isArray(entry.podMainOverlays) ? entry.podMainOverlays : [],
        listingGroupIndex: Number.isFinite(Number(entry.listingGroupIndex))
          ? Number(entry.listingGroupIndex)
          : undefined,
        mainImageCount: entry.mainImageCount,
        detailPageCount: entry.detailPageCount,
        previewLeftLabel: '本步输入',
        previewRightLabel: nav === 'podProductDesign' ? 'POD商品设计' : '主图创作',
        inputImageUrl,
        outputImageUrl,
      };
    }
    if (nav === 'aiCopywriting' || nav === 'titles' || nav === 'titleExtract') {
      const text = entry.content || '';
      return {
        ...base,
        titleTaskId: entry.taskId,
        titleText: text,
        copywritingTaskId: entry.taskId,
        copywritingText: text,
        language: entry.language,
        note: entry.note || (text ? `文案：${text}` : 'AI文案已完成'),
        previewLeftLabel: '参考主图（流水线当前图）',
        previewRightLabel: '同左（本步产出为文案文本，见下方说明）',
        inputImageUrl: input.url,
        outputImageUrl: input.url,
      };
    }
    if (nav === 'infringementDetection') {
      return {
        navId: nav,
        label: step.label,
        status: 'ok',
        infringementBatchId: entry.batchId,
        infringementBatchNo: entry.batchNo,
        riskLevel: entry.riskLevel,
        highRisk: Boolean(entry.highRisk),
        note: entry.note,
        previewLeftLabel: '检测原图',
        previewRightLabel: `检测结果（${entry.riskLevel || '—'}）`,
        inputImageUrl,
        outputImageUrl: inputImageUrl,
      };
    }
    if (nav === 'platformListing') {
      return {
        navId: nav,
        label: step.label,
        status: 'ok',
        listingJobId: entry.listingJobId ?? null,
        listingJobIds: entry.listingJobIds || [],
        sourcingItemIds: entry.sourcingItemIds || [],
        batchKey: entry.batchKey,
        shopName: entry.shopName,
        listingAutoRuleNames: entry.listingAutoRuleNames || [],
        note: entry.note,
        previewLeftLabel: '本步输入',
        previewRightLabel: '已入库 / 已创建刊登',
        inputImageUrl,
        outputImageUrl: inputImageUrl,
      };
    }
    if (nav === 'aiTextToImage') {
      return {
        ...base,
        compositeImageUrls: entry.compositeImageUrls || [],
        previewLeftLabel: '提示词参考（上游当前图）',
        previewRightLabel: '文生图产出',
        inputImageUrl,
        outputImageUrl,
      };
    }
    if (nav === 'imageFission') {
      return {
        ...base,
        compositeImageUrls: entry.compositeImageUrls || [],
        previewLeftLabel: '本步输入（原图）',
        previewRightLabel: '裂变产出',
        inputImageUrl,
        outputImageUrl,
      };
    }
    return {
      ...base,
      compositeImageUrls: entry.compositeImageUrls || [],
      previewLeftLabel: '本步输入',
      previewRightLabel: '本步产出',
      inputImageUrl,
      outputImageUrl,
    };
  };

  const dispatchStageBatch = async ({ batchNav, nav, step, params, sources, workflowRemark }) => {
    const batchContext = {
      ...baseWorkflowContext,
      workflowRunId,
      workflowStageBatch: true,
      batchSourceCount: sources.length,
    };
    const attachTask = (res) => ({
      ...res,
      bySource: (res.bySource || []).map((entry) => ({
        taskId: res.taskId,
        ...entry,
      })),
    });

    if (batchNav === 'imageCrop') return attachTask(await runCropBatchStep(sources, batchContext));
    if (batchNav === 'imageEnhance') return attachTask(await runEnhanceBatchStep(sources, batchContext));
    if (batchNav === 'stampExtract') return attachTask(await runStampExtractBatchStep(sources, batchContext));
    if (batchNav === 'smartCutout') return attachTask(await runCutoutBatchStep(sources, params, batchContext));
    if (batchNav === 'goods' || batchNav === 'podProductDesign') {
      const res = await runGoodsBatchWorkflowStep({
        groups: sources,
        params,
        workflowContext: batchContext,
        billingNav: nav,
      });
      return attachTask({ taskId: res.taskId, bySource: res.byGroup || [] });
    }
    if (batchNav === 'aiCopywriting') {
      return attachTask(await runAiCopywritingBatchWorkflowStep({
        sources,
        params,
        workflowContext: batchContext,
      }));
    }
    if (batchNav === 'img2img' || batchNav === 'aiOutpaint') {
      return attachTask(await runAiImageBatchWorkflowStep({
        navId: nav,
        sources,
        params,
        workflowContext: batchContext,
      }));
    }
    if (batchNav === 'aiTextToImage') {
      return attachTask(await runAiTextToImageBatchWorkflowStep({
        sources,
        params,
        workflowContext: batchContext,
      }));
    }
    if (batchNav === 'imageFission' || batchNav === 'patternFission') {
      return attachTask(await runFissionBatchWorkflowStep({
        sources,
        params: batchNav === 'patternFission' ? { ...params, patternOnly: true } : params,
        workflowContext: batchContext,
      }));
    }
    if (isModelStudioNav(nav)) {
      return attachTask(await runModelStudioBatchWorkflowStep({
        navId: nav,
        sources,
        params,
        workflowContext: batchContext,
      }));
    }
    if (batchNav === 'infringementDetection') {
      return attachTask(await runInfringementBatchStep(sources, params, {
        onRiskPrompt: onInfringementRiskPrompt
          ? (ctx) => onInfringementRiskPrompt({
            ...ctx,
            row: effectiveRows[ctx.source?.__i],
            rowIndex: ctx.source?.rowIndex,
          })
          : undefined,
      }, batchContext));
    }
    if (batchNav === 'platformListing') {
      const shared = await runPlatformListingBatchStep(
        sources.map((s) => ({
          row: effectiveRows[s.__i],
          url: s.url,
          result: prevStageResult[s.__i],
        })),
        params,
        workflowRemark,
      );
      return { bySource: sources.map(() => ({ ...shared })) };
    }
    throw new Error(i18n.t('workbench:ipeUtilError.stepNotImplementedBatch', { nav }));
  };

  /** 执行「一个模块一个批次」。返回 true 表示本阶段已整体处理。 */
  const tryRunStageBatch = async (stageIndex, active) => {
    const stageSteps = stageDefs[stageIndex] || [];
    if (stageSteps.length !== 1) return false;
    const step = stageSteps[0];
    const nav = resolveWorkflowNavId(step);
    if (!STAGE_BATCH_NAV_IDS.has(nav)) return false;

    const firstRow = effectiveRows[active[0]];
    // 批次参数用模板步骤配置；各组印花图不同，不能因为 stepOverrides 不一致就拆成 N 个任务
    const params = resolveStepParams(step, firstRow);

    active.forEach((i) => {
      onStep?.({
        rowIndex: resolveRowIndex(i),
        row: effectiveRows[i],
        total,
        stageIndex,
        stageTotal: stageCount,
        index: stageIndex,
        navId: nav,
        label: step.label,
        status: 'running',
        inputImageUrl: prevStageResult[i]?.finalImageUrl || '',
        outputImageUrl: '',
      });
    });

    // 1) 解析各条目本阶段输入
    const inputs = new Map();
    const invalidRows = [];
    for (const i of active) {
      const prev = prevStageResult[i];
      if (prev && (prev.finalSpaceId != null || prev.finalImageUrl != null)) {
        inputs.set(i, { spaceId: prev.finalSpaceId, url: prev.finalImageUrl });
        continue;
      }
      const resume = rowResumeOptions[i]?.resumeFromResult;
      if (resume && (resume.finalSpaceId || resume.finalImageUrl)) {
        inputs.set(i, {
          spaceId: resume.finalSpaceId,
          url: resume.finalImageUrl,
          initialImageUrl: resume.initialImageUrl,
        });
        continue;
      }
      try {
        abortIfNeeded();
        const init = await withWorkflowStepRetry(() => ensureInitialSpace(effectiveRows[i]));
        inputs.set(i, { spaceId: init.spaceId, url: init.imageUrl, initialImageUrl: init.imageUrl });
      } catch (e) {
        invalidRows.push({ i, error: e?.message || String(e) });
      }
    }
    invalidRows.forEach(({ i, error }) => settleRowFailure(i, error, stageIndex, step, nav, true));

    const sources = [];
    if (nav === 'goods') {
      // 主图列表若在步骤上被截短，从 POD 任务回读完整黑/白色位
      await enrichPodMainEntriesFromGoodsTasks(active, prevStageResult);
      // 按 POD listing 组去重：一组多印花只带入该组模板主图（颜色位数），在一个批次里跑
      const listingBuckets = new Map();
      active.forEach((i) => {
        const input = inputs.get(i);
        if (!input || !input.url) return;
        const prev = prevStageResult[i];
        const podEntries = collectPodMainEntriesFromPrevResult(prev);
        const key = resolvePodListingGroupKey(prev, i);
        let bucket = listingBuckets.get(key);
        if (!bucket) {
          bucket = {
            key,
            primaryI: i,
            members: [],
            mainEntries: podEntries,
            fromPodMains: podEntries.length > 0,
          };
          listingBuckets.set(key, bucket);
        }
        bucket.members.push(i);
        // 取同组里最完整的主图列表（避免首行尚未写全黑/白元数据）
        if (podEntries.length > (bucket.mainEntries?.length || 0)) {
          bucket.mainEntries = podEntries;
          bucket.fromPodMains = true;
        }
      });
      listingBuckets.forEach((bucket) => {
        const primaryInput = inputs.get(bucket.primaryI);
        if (!primaryInput?.url) return;
        const rowName = String(
          effectiveRows[bucket.primaryI]?.title || effectiveRows[bucket.primaryI]?.name || '',
        ).trim();
        const mainEntries = bucket.fromPodMains && bucket.mainEntries.length > 0
          ? bucket.mainEntries
          : [{ url: primaryInput.url, colorLabel: '', colorCode: '', mainName: '' }];
        mainEntries.forEach((entry, podMainSlot) => {
          const url = String(entry?.url || '').trim();
          if (!url) return;
          sources.push({
            rowId: effectiveRows[bucket.primaryI].id,
            rowIndex: resolveRowIndex(bucket.primaryI),
            spaceId: podMainSlot === 0 ? primaryInput.spaceId : undefined,
            url,
            name: rowName,
            title: rowName,
            __i: bucket.primaryI,
            __memberRows: [...bucket.members],
            __podMainSlot: podMainSlot,
            __podMainCount: mainEntries.length,
            __fromPodMains: Boolean(bucket.fromPodMains),
            __listingGroupKey: bucket.key,
            __colorLabel: String(entry?.colorLabel || '').trim(),
            __colorCode: String(entry?.colorCode || '').trim(),
            __mainName: String(entry?.mainName || '').trim(),
          });
        });
      });
    } else {
      active.forEach((i) => {
        const input = inputs.get(i);
        if (!input || !input.url) return;
        const rowName = String(effectiveRows[i]?.title || effectiveRows[i]?.name || '').trim();
        sources.push({
          rowId: effectiveRows[i].id,
          rowIndex: resolveRowIndex(i),
          spaceId: input.spaceId,
          url: input.url,
          name: rowName,
          title: rowName,
          __i: i,
          __memberRows: [i],
        });
      });
    }
    if (sources.length === 0) {
      if (stageIndex === stageCount - 1) {
        localIndices.forEach((i) => {
          if (recorded[i] || prevStageResult[i] == null) return;
          recordRowDone(i, prevStageResult[i]);
        });
      }
      return true;
    }

    // 来自 POD 主图时：每张模板主图只出 1 张效果图（忽略步骤里可能保存的 mainImageCount=2）
    const goodsParams = { ...params };
    if (nav === 'goods' && sources.some((s) => s.__fromPodMains)) {
      goodsParams.mainImageCount = 1;
      goodsParams.detailPageCount = 0;
    }

    console.log(
      `[工作流阶段优先] 模块 ${stageIndex + 1}/${stageCount}「${step.label || nav}」合并为 1 个批次任务，含 ${sources.length} 个条目`,
    );
    // 2) 标记处理中（含同 listing 组的印花行）
    const runningMarked = new Set();
    sources.forEach((s) => {
      const members = Array.isArray(s.__memberRows) && s.__memberRows.length
        ? s.__memberRows
        : [s.__i];
      members.forEach((memberI) => {
        if (runningMarked.has(memberI)) return;
        runningMarked.add(memberI);
        onStep?.({
          rowIndex: resolveRowIndex(memberI),
          row: effectiveRows[memberI],
          total,
          stageIndex,
          stageTotal: stageCount,
          index: stageIndex,
          navId: nav,
          label: step.label,
          status: 'running',
          inputImageUrl: inputs.get(memberI)?.url || '',
          outputImageUrl: '',
        });
      });
    });

    // 3) 一个模块一个批次
    let batchOut = null;
    try {
      abortIfNeeded();
      batchOut = await withWorkflowStepRetry(() => dispatchStageBatch({
        batchNav: nav,
        nav,
        step,
        params: nav === 'goods' ? goodsParams : params,
        sources,
        workflowRemark,
      }));
    } catch (e) {
      if (e?.name === 'WorkflowTaskAbortedError' || e?.message === '任务已终止') throw e;
      const msg = e?.message || String(e);
      const policy = normalizeStepFailurePolicy(step.onFailure);
      const isSkip = policy === STEP_FAILURE_POLICY_SKIP;
      let terminateErr = null;
      const failedRows = new Set();
      for (const s of sources) {
        const members = Array.isArray(s.__memberRows) && s.__memberRows.length
          ? s.__memberRows
          : [s.__i];
        members.forEach((memberI) => {
          if (failedRows.has(memberI)) return;
          failedRows.add(memberI);
          const err = settleRowFailure(memberI, msg, stageIndex, step, nav, isSkip, {
            throwOnTerminate: false,
            note: e?.note || msg,
            batchKey: e?.batchKey,
            listingJobId: e?.listingJobId,
            listingAutoRuleNames: e?.listingAutoRuleNames,
            sourcingItemIds: e?.sourcingItemIds,
          });
          if (err) terminateErr = terminateErr || err;
        });
      }
      if (stageIndex === stageCount - 1) {
        localIndices.forEach((i) => {
          if (recorded[i] || prevStageResult[i] == null) return;
          recordRowDone(i, prevStageResult[i]);
        });
      }
      if (terminateErr) throw terminateErr;
      return true;
    }

    // 4) 把批次产出切回各条目（同一行展开的多主图 source 合并为一条步骤）
    const bySource = Array.isArray(batchOut?.bySource) ? batchOut.bySource : [];
    const partsByRow = new Map();
    sources.forEach((s, k) => {
      const list = partsByRow.get(s.__i) || [];
      list.push({ source: s, entry: bySource[k] || {}, k });
      partsByRow.set(s.__i, list);
    });

    const orderedRowIndices = [];
    sources.forEach((s) => {
      if (!orderedRowIndices.includes(s.__i)) orderedRowIndices.push(s.__i);
    });

    for (let rowPos = 0; rowPos < orderedRowIndices.length; rowPos += 1) {
      const i = orderedRowIndices[rowPos];
      const parts = partsByRow.get(i) || [];
      const input = inputs.get(i);
      const firstSource = parts[0]?.source || { rowIndex: resolveRowIndex(i) };

      const memberRows = Array.isArray(firstSource.__memberRows) && firstSource.__memberRows.length
        ? [...new Set(firstSource.__memberRows)]
        : [i];

      if (parts.some((p) => p.entry.skipped) && parts.every((p) => p.entry.skipped || p.entry.failed)) {
        memberRows.forEach((memberI) => {
          settleRowFailure(
            memberI,
            parts[0]?.entry?.note || '已按用户选择跳过当前条目',
            stageIndex,
            step,
            nav,
            true,
          );
        });
        continue;
      }

      const failedPart = parts.find((p) => p.entry.failed);
      if (failedPart && !parts.some((p) => !p.entry.failed && !p.entry.skipped)) {
        const policy = normalizeStepFailurePolicy(step.onFailure);
        const isSkip = policy === STEP_FAILURE_POLICY_SKIP;
        const failMsg = failedPart.entry.error || `${step.label || nav}失败`;
        let groupTerminateErr = null;
        for (let mi = 0; mi < memberRows.length; mi += 1) {
          const err = settleRowFailure(
            memberRows[mi],
            failMsg,
            stageIndex,
            step,
            nav,
            isSkip,
            { throwOnTerminate: false },
          );
          if (err) groupTerminateErr = groupTerminateErr || err;
        }
        if (groupTerminateErr) {
          for (let rest = rowPos + 1; rest < orderedRowIndices.length; rest += 1) {
            const restParts = partsByRow.get(orderedRowIndices[rest]) || [];
            const restMembers = Array.isArray(restParts[0]?.source?.__memberRows)
              && restParts[0].source.__memberRows.length
              ? restParts[0].source.__memberRows
              : [orderedRowIndices[rest]];
            for (let ri = 0; ri < restMembers.length; ri += 1) {
              settleRowFailure(
                restMembers[ri],
                failMsg,
                stageIndex,
                step,
                nav,
                false,
                { throwOnTerminate: false },
              );
            }
          }
          throw groupTerminateErr;
        }
        continue;
      }

      const okParts = parts.filter((p) => !p.entry.failed && !p.entry.skipped);
      const primary = okParts[0]?.entry || parts[0]?.entry || {};
      const mergedUrls = uniqueListingUrls(okParts.flatMap((p) => (
        Array.isArray(p.entry.compositeImageUrls) && p.entry.compositeImageUrls.length
          ? p.entry.compositeImageUrls
          : [p.entry.imageUrl]
      )));
      const mergedMains = uniqueListingUrls(okParts.flatMap((p) => (
        Array.isArray(p.entry.mainCompositeImageUrls)
          ? p.entry.mainCompositeImageUrls
          : []
      )));
      // 每条 POD 主图 source → 对应创作产出，供带入商品库时按颜色对齐
      const podMainOverlays = okParts
        .map((p) => {
          const sourceUrl = String(p.source?.url || '').trim();
          const outputUrl = String(
            (Array.isArray(p.entry.compositeImageUrls) && p.entry.compositeImageUrls[0])
            || p.entry.imageUrl
            || '',
          ).trim();
          if (!sourceUrl || !outputUrl) return null;
          if (!(p.source?.__fromPodMains || p.source?.__podMainSlot != null)) return null;
          return { sourceUrl, outputUrl };
        })
        .filter(Boolean);
      const mergedMetas = okParts.flatMap((p) => (
        Array.isArray(p.entry.mainCompositeMetas) ? p.entry.mainCompositeMetas : []
      ));
      const entry = {
        ...primary,
        imageUrl: primary.imageUrl || mergedUrls[0] || '',
        spaceId: primary.spaceId,
        compositeImageUrls: mergedUrls.length ? mergedUrls : (primary.compositeImageUrls || []),
        mainCompositeImageUrls: mergedMains.length
          ? mergedMains
          : (Array.isArray(primary.mainCompositeImageUrls) ? primary.mainCompositeImageUrls : []),
        mainCompositeMetas: mergedMetas.length
          ? mergedMetas
          : (Array.isArray(primary.mainCompositeMetas) ? primary.mainCompositeMetas : []),
        podMainOverlays,
      };

      // 同 listing 组内印花行共享同一套主图创作结果
      memberRows.forEach((memberI) => {
        const memberInput = inputs.get(memberI) || input;
        const record = withStepOutputDisplay(
          buildBatchStepRecord(nav, step, entry, memberInput),
          entry,
        );
        const base = resolveRowBase(memberI, memberInput);
        const next = {
          ...base,
          ok: true,
          error: null,
          steps: [...(base.steps || []), record],
        };
        if (nav === 'aiCopywriting' || nav === 'titles' || nav === 'titleExtract') {
          next.titleTaskId = entry.taskId;
          next.titleText = entry.content;
          next.copywritingTaskId = entry.taskId;
          next.copywritingText = entry.content;
          next.copywritingLanguage = entry.language;
        }
        if (nav === 'infringementDetection') {
          next.infringementBatchId = entry.batchId;
          if (entry.highRisk) {
            next.infringementHighRisk = true;
            next.infringementRiskLevel = entry.riskLevel;
            next.infringementNote = entry.note;
          }
        }
        if (nav === 'platformListing') {
          next.listingJobId = entry.listingJobId ?? null;
          next.sourcingItemIds = entry.sourcingItemIds || [];
        }
        if (nav === 'stampExtract') {
          next.stampOutputSpaceId = entry.spaceId;
          next.stampOutputUrl = entry.imageUrl;
        }
        if (!next.initialImageUrl) next.initialImageUrl = memberInput.url;
        if (NON_ADVANCING_NAV_IDS.has(nav)) {
          next.finalSpaceId = base.finalSpaceId ?? memberInput.spaceId;
          next.finalImageUrl = base.finalImageUrl ?? memberInput.url;
        } else {
          next.finalSpaceId = entry.spaceId;
          next.finalImageUrl = entry.imageUrl;
        }
        prevStageResult[memberI] = next;
        onStep?.({
          rowIndex: resolveRowIndex(memberI),
          row: effectiveRows[memberI],
          total,
          stageIndex,
          stageTotal: stageCount,
          index: stageIndex,
          navId: nav,
          label: step.label,
          status: record.highRisk ? 'risk' : 'done',
          detail: record.note || '',
          inputImageUrl: record.inputImageUrl,
          outputImageUrl: record.outputImageUrl,
          outputImageUrlDisplay: record.outputImageUrlDisplay || '',
          viewUrl: record.viewUrl || '',
          displayUrl: record.displayUrl || record.viewUrl || '',
          compositeImageUrls: record.compositeImageUrls || [],
          compositeImageViewUrls: record.compositeImageViewUrls || [],
          previewLeftLabel: record.previewLeftLabel,
          previewCenterLabel: record.previewCenterLabel || '',
          previewRightLabel: record.previewRightLabel,
          outputVideoUrl: record.outputVideoUrl || '',
          outputVideoUrls: Array.isArray(record.outputVideoUrls) ? record.outputVideoUrls : [],
          taskId: record.taskId || '',
          goodsTaskId: record.goodsTaskId || '',
          videoTaskId: record.videoTaskId || '',
          copywritingTaskId: record.copywritingTaskId || record.titleTaskId || '',
          listingJobId: record.listingJobId || '',
          listingJobIds: Array.isArray(record.listingJobIds) ? record.listingJobIds : [],
          listingAutoRuleNames: Array.isArray(record.listingAutoRuleNames) ? record.listingAutoRuleNames : [],
          sourcingItemIds: Array.isArray(record.sourcingItemIds) ? record.sourcingItemIds : [],
          infringementBatchId: record.infringementBatchId || '',
          batchId: record.batchId || record.infringementBatchId || '',
          sourcingBatchKey: record.sourcingBatchKey || record.batchKey || '',
          batchKey: record.batchKey || record.sourcingBatchKey || '',
        });
        if (stageIndex < stageCount - 1) {
          emitRowProgress(memberI, next, true);
        }
      });
    }

    if (stageIndex === stageCount - 1) {
      localIndices.forEach((i) => {
        if (recorded[i] || prevStageResult[i] == null) return;
        recordRowDone(i, prevStageResult[i]);
      });
    }
    return true;
  };

  localIndices.forEach((i) => {
    const resume = rowResumeOptions[i]?.resumeFromResult;
    const rowStart = Math.max(0, Number(rowResumeOptions[i]?.startStepIndex) || 0);
    if (resume && rowStart > 0) {
      prevStageResult[i] = resolveRowBase(i, {
        spaceId: resume.finalSpaceId,
        url: resume.finalImageUrl,
      });
    }
  });

  // 首个模块开始前统一初始化条目（续跑时保留已完成步骤）
  localIndices.forEach((i) => {
    abortIfNeeded();
    onRowStart?.({ rowIndex: resolveRowIndex(i), row: effectiveRows[i], total });
  });

  for (let stageIndex = startStage; stageIndex < stageCount; stageIndex += 1) {
    abortIfNeeded();
    const active = localIndices.filter((i) => {
      if (stopped[i]) return false;
      const rowStart = Math.max(0, Number(rowResumeOptions[i]?.startStepIndex) || 0);
      if (stageIndex < rowStart) return false;
      if (stageIndex === startStage || stageIndex === rowStart) return true;
      return prevStageResult[i] != null;
    });
    if (active.length === 0) break;

    if (await tryRunStageBatch(stageIndex, active)) continue;

    const stageNav = resolveWorkflowNavId(stageDefs[stageIndex]?.[0]);
    if (STAGE_BATCH_NAV_IDS.has(stageNav)) {
      console.warn(
        `[工作流阶段优先] 模块「${stageNav}」未能合并批次，仍禁止拆成多组任务，跳过本阶段逐组回退`,
      );
      continue;
    }

    for (let batchStart = 0; batchStart < active.length; batchStart += MAX_BATCH_CONCURRENT) {
      abortIfNeeded();
      const batchEnd = Math.min(batchStart + MAX_BATCH_CONCURRENT, active.length);
      const batch = active.slice(batchStart, batchEnd);
      const batchIndex = Math.floor(batchStart / MAX_BATCH_CONCURRENT) + 1;
      console.log(
        `[工作流阶段优先] 模块 ${stageIndex + 1}/${stageCount} 第 ${batchIndex} 批，处理条目 ${batch.map((i) => resolveRowIndex(i) + 1).join('、')}`,
      );

      let settled;
      try {
        settled = await Promise.all(batch.map(async (i) => {
          const row = effectiveRows[i];
          const rowIndex = resolveRowIndex(i);
          try {
            const result = await withWorkflowStepRetry(() => runPipelineForRow(row, template, {
              rowIndex,
              workflowRunId,
              workflowContext: baseWorkflowContext,
              shouldAbort,
              onStep: (ev) => onStep?.({
                rowIndex, row, total, stageIndex, stageTotal: stageCount, ...ev,
              }),
              onInfringementRiskPrompt,
              onGoodsStampAssignPrompt,
              workflowRemark,
              startStepIndex: stageIndex,
              endStepIndex: stageIndex,
              resumeFromResult: stageIndex === startStage
                ? (rowResumeOptions[i]?.resumeFromResult || null)
                : prevStageResult[i],
            }));
            return { i, result };
          } catch (e) {
            if (e?.name === 'WorkflowManualReviewRequiredError') {
              return { i, result: e.result || { ok: false, pendingReview: true } };
            }
            if (e?.name === 'WorkflowTaskAbortedError' || e?.message === '任务已终止') {
              throw e;
            }
            if (e?.batchTerminated || e?.name === 'WorkflowBatchTerminatedError') {
              e.stageFirstRowIndex = rowIndex;
              throw e;
            }
            if (e?.skipRow) {
              const msg = e.message || String(e);
              console.log(`[工作流阶段优先] 条目 ${rowIndex + 1}/${total} 已跳过: ${msg}`);
              return { i, result: { ok: false, error: msg, steps: [], skipped: true } };
            }
            const msg = e.message || String(e);
            console.error(`[工作流阶段优先] 条目 ${rowIndex + 1}/${total} 异常:`, msg);
            return { i, result: { ok: false, error: msg, steps: [] } };
          }
        }));
      } catch (e) {
        if (e?.batchTerminated || e?.name === 'WorkflowBatchTerminatedError') {
          const failedRowIndex = Number(e.stageFirstRowIndex);
          const failedLocal = Number.isFinite(failedRowIndex)
            ? batch.find((i) => resolveRowIndex(i) === failedRowIndex)
            : undefined;
          if (failedLocal != null && e.result) {
            prevStageResult[failedLocal] = e.result;
            recordRowDone(failedLocal, e.result);
          }
        }
        throw e;
      }

      settled.forEach(({ i, result }) => {
        prevStageResult[i] = result;
        if (result?.pendingReview || result?.skipped || result?.ok === false) {
          recordRowDone(i, result);
        } else if (stageIndex < stageCount - 1) {
          emitRowProgress(i, result, true);
        }
      });

      if (batchEnd < active.length) {
        console.log(`[工作流阶段优先] 模块 ${stageIndex + 1} 第 ${batchIndex} 批完成，等待 ~${BATCH_DELAY_MS}ms 后继续下一批...`);
        await sleepWithJitter(BATCH_DELAY_MS);
      }
    }

    // 最后一个模块跑完，收尾记录所有仍在进行中的条目
    if (stageIndex === stageCount - 1) {
      localIndices.forEach((i) => {
        if (recorded[i] || prevStageResult[i] == null) return;
        recordRowDone(i, prevStageResult[i]);
      });
    }
  }

  localIndices.forEach((i) => {
    if (recorded[i]) return;
    recordRowDone(i, prevStageResult[i] || { ok: false, error: i18n.t('workbench:ipeUtilError.stepNotExecuted'), steps: [] });
  });

  console.log(`[工作流阶段优先] 全部 ${total} 个条目处理完成`);
  return finalEntries.filter(Boolean);
}

/**
 * 批量执行：分批并发处理，每批最多 MAX_BATCH_CONCURRENT 个条目
 * 一批完成后再跑下一批，避免触发 API 限流
 */

export async function runPipelineForRows(rows, template, {
  onRowStart,
  onStep,
  onRowDone,
  onRowProgress,
  onInfringementRiskPrompt,
  onGoodsStampAssignPrompt,
  getRowWithOverrides,
  getRowResumeOptions,
  workflowRemark = '',
  workflowRunId,
  workflowContext: workflowContextInput,
  shouldAbort,
  rowIndexOffset = 0,
  rowIndices,
  totalRows,
} = {}) {
  const baseWorkflowContext = workflowContextInput || { workflowRunId };
  const results = [];
  const total = totalRows ?? rows.length;
  const useSparseIndices = Array.isArray(rowIndices) && rowIndices.length === rows.length;

  const resolveRowIndex = (localIndex) => (
    useSparseIndices ? rowIndices[localIndex] : rowIndexOffset + localIndex
  );

  // 始终按「模块优先」串行：一个模块处理完全部印花组（1 个批次任务），再进入下一模块。
  const stageDefs = resolveTemplateStepStages(template)
    .map((stage) => stage.filter((s) => s.navId !== 'sourcingHub'))
    .filter((stage) => stage.length > 0)
    .flat()
    .map((step) => [step]);
  const rowResumeOptions = rows.map((_, i) => {
    const rowIndex = resolveRowIndex(i);
    return typeof getRowResumeOptions === 'function'
      ? (getRowResumeOptions(rowIndex) || {})
      : {};
  });
  if (stageDefs.length >= 1) {
    const effectiveRows = rows.map((row, i) => (
      getRowWithOverrides ? getRowWithOverrides(row, resolveRowIndex(i)) : row
    ));
    const startStage = rowResumeOptions.reduce((min, opt) => {
      const idx = Math.max(0, Number(opt?.startStepIndex) || 0);
      return Math.min(min, idx);
    }, Math.max(0, Number(rowResumeOptions[0]?.startStepIndex) || 0));
    console.log(`[工作流阶段优先] 共 ${stageDefs.length} 个模块，按「一个模块一个批次」处理 ${rows.length} 组印花`);
    return runPipelineStageFirst(effectiveRows, template, {
      onRowStart,
      onStep,
      onRowDone,
      onRowProgress,
      onInfringementRiskPrompt,
      onGoodsStampAssignPrompt,
      workflowRemark,
      workflowRunId,
      baseWorkflowContext,
      shouldAbort,
      resolveRowIndex,
      total,
      stageCount: stageDefs.length,
      startStage,
      rowResumeOptions,
    });
  }

  // 分批处理
  for (let batchStart = 0; batchStart < rows.length; batchStart += MAX_BATCH_CONCURRENT) {
    if (typeof shouldAbort === 'function' && shouldAbort()) {
      const err = new Error('任务已终止');
      err.name = 'WorkflowTaskAbortedError';
      throw err;
    }

    const batchEnd = Math.min(batchStart + MAX_BATCH_CONCURRENT, rows.length);
    const batchRows = rows.slice(batchStart, batchEnd);
    const batchIndex = Math.floor(batchStart / MAX_BATCH_CONCURRENT) + 1;
    const totalBatches = Math.ceil(rows.length / MAX_BATCH_CONCURRENT);

    console.log(`[工作流分批执行] 第 ${batchIndex}/${totalBatches} 批，处理条目 ${resolveRowIndex(batchStart) + 1}-${resolveRowIndex(batchEnd - 1) + 1}`);

    // 并发执行当前批（单条异常不拖垮整批）
    const batchPromises = batchRows.map(async (row, batchOffset) => {
      const rowIndex = resolveRowIndex(batchStart + batchOffset);
      throwIfAborted(shouldAbort);
      const effectiveRow = getRowWithOverrides ? getRowWithOverrides(row, rowIndex) : row;
      onRowStart?.({ rowIndex, row: effectiveRow, total });

      try {
        const resumeOptions = typeof getRowResumeOptions === 'function'
          ? (getRowResumeOptions(rowIndex) || {})
          : {};
        const r = await withWorkflowStepRetry(async () =>
            runPipelineForRow(effectiveRow, template, {
              rowIndex,
              workflowRunId,
              workflowContext: baseWorkflowContext,
              shouldAbort,
              onStep: (ev) => onStep?.({ rowIndex, row: effectiveRow, total, ...ev }),
              onInfringementRiskPrompt,
              onGoodsStampAssignPrompt,
              workflowRemark,
              startStepIndex: resumeOptions.startStepIndex || 0,
              resumeFromResult: resumeOptions.resumeFromResult || null,
            }),
        );
        throwIfAborted(shouldAbort);
        if (r?.skipped) {
          console.log(`[工作流分批执行] 条目 ${rowIndex + 1}/${total} 已跳过: ${r.error || '侵权风险'}`);
        }
        onRowDone?.({ rowIndex, row: effectiveRow, total, result: r });
        if (r?.pendingReview) {
          console.log(
            `[工作流分批执行] 条目 ${rowIndex + 1}/${total} 步骤「${r.reviewStepLabel || ''}」已完成，等待人工审核（其余条目继续）`,
          );
        }
        return { row: effectiveRow, result: r };
      } catch (e) {
        if (e?.name === 'WorkflowManualReviewRequiredError') {
          return { row: effectiveRow, result: e.result || { ok: false, pendingReview: true } };
        }
        if (e?.name === 'WorkflowTaskAbortedError' || e?.message === '任务已终止') {
          throw e;
        }
        if (e?.batchTerminated || e?.name === 'WorkflowBatchTerminatedError') {
          // 步骤失败按模板设置终止整批：先落当前条目结果，再中断整批
          if (e.result) {
            onRowDone?.({ rowIndex, row: effectiveRow, total, result: e.result });
          }
          throw e;
        }
        if (e?.skipRow) {
          const msg = e.message || String(e);
          const skippedResult = { ok: false, error: msg, steps: [], skipped: true };
          console.log(`[工作流分批执行] 条目 ${rowIndex + 1}/${total} 已跳过: ${msg}`);
          return { row: effectiveRow, result: skippedResult };
        }
        const msg = e.message || String(e);
        const fail = { ok: false, error: msg, steps: [] };
        console.error(`[工作流分批执行] 条目 ${rowIndex + 1}/${total} 异常:`, msg);
        onRowDone?.({ rowIndex, row, total, result: fail });
        return { row, result: fail };
      }
    });

    // 等待当前批全部完成（任一条终止则整批中断）
    const batchResults = await Promise.all(batchPromises);
    results.push(...batchResults);

    // 批次间延迟（最后一批不需要延迟）
    // 添加随机抖动，避免批次间请求同步触发
    if (batchEnd < rows.length) {
      console.log(`[工作流分批执行] 第 ${batchIndex} 批完成，等待 ~${BATCH_DELAY_MS}ms 后继续下一批...`);
      await sleepWithJitter(BATCH_DELAY_MS);
    }
  }

  console.log(`[工作流分批执行] 全部 ${total} 个条目处理完成`);
  return results;
}
